<template>
    <v-container fluid class="proceedings-shadcn proceeding-details-page pa-6">
        <div
            v-if="initialLoading"
            class="proceeding-detail-skeleton"
            role="status"
            aria-live="polite"
            aria-label="Carregando detalhes da impugnação"
        >
            <span class="sr-only">Carregando detalhes da impugnação</span>

            <div class="detail-skeleton-toolbar">
                <div class="skeleton-line skeleton-breadcrumb" />
                <div class="detail-skeleton-actions">
                    <div class="skeleton-block skeleton-action skeleton-action-wide" />
                    <div class="skeleton-block skeleton-action" />
                    <div class="skeleton-block skeleton-action skeleton-action-wide" />
                </div>
            </div>

            <section class="detail-skeleton-card detail-skeleton-hero">
                <div class="skeleton-line skeleton-eyebrow" />
                <div class="skeleton-line skeleton-title" />
                <div class="skeleton-line skeleton-status" />
                <div class="detail-skeleton-facts">
                    <div v-for="index in 4" :key="`hero-fact-${index}`" class="skeleton-block skeleton-fact">
                        <div class="skeleton-line skeleton-fact-label" />
                        <div class="skeleton-line skeleton-fact-value" />
                    </div>
                </div>
            </section>

            <section class="detail-skeleton-card detail-skeleton-tabs">
                <div v-for="index in 5" :key="`tab-${index}`" class="skeleton-line skeleton-tab" />
            </section>

            <section class="detail-skeleton-card detail-skeleton-content">
                <div class="detail-skeleton-section-heading">
                    <div class="skeleton-line skeleton-section-title" />
                    <div class="skeleton-line skeleton-pending" />
                </div>
                <div class="skeleton-block skeleton-toggle" />
                <div class="detail-skeleton-fields detail-skeleton-fields-main">
                    <div v-for="index in 3" :key="`main-field-${index}`" class="skeleton-block skeleton-field" />
                </div>
                <div class="detail-skeleton-fields">
                    <div v-for="index in 4" :key="`field-${index}`" class="skeleton-block skeleton-field" />
                </div>
                <div class="detail-skeleton-fields">
                    <div v-for="index in 4" :key="`location-field-${index}`" class="skeleton-block skeleton-field" />
                </div>
                <div class="skeleton-block skeleton-textarea" />
            </section>
        </div>

        <template v-else>
        <!-- Header -->
        <header class="details-toolbar">
            <div class="details-toolbar-context">
                <v-breadcrumbs class="app-breadcrumb details-breadcrumb" divider="›" :items="dataset.breadcrumbItems"></v-breadcrumbs>
                <span v-if="page.isNew" class="details-toolbar-title">{{ page.title }}</span>
            </div>
            <div class="details-toolbar-actions">
                <v-menu offset-y v-if="!page.isNew" content-class="details-status-menu">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn class="details-status-button" elevation="0" outlined v-bind="attrs" v-on="on"
                            :color="dataset.data.status === 1 ? 'success' : 'primary'">
                            <v-icon left small>{{ dataset.data.status === 1 ? 'mdi-check-circle-outline' : 'mdi-progress-clock' }}</v-icon>
                            {{ dataset.data.status === 1 ? 'Finalizado' : 'Em andamento' }}
                            <v-icon right small>mdi-chevron-down</v-icon>
                        </v-btn>
                    </template>
                    <v-list dense>
                        <v-list-item @click="changeStatus(0)" :disabled="dataset.data.status === 0">
                            <v-list-item-icon class="mr-2"><v-icon small color="primary">mdi-progress-clock</v-icon></v-list-item-icon>
                            <v-list-item-title>Em andamento</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="changeStatus(1)" :disabled="dataset.data.status === 1">
                            <v-list-item-icon class="mr-2"><v-icon small color="success">mdi-check-circle-outline</v-icon></v-list-item-icon>
                            <v-list-item-title>Finalizado</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
                <v-btn id="btnBack" class="details-secondary-action" type="button" elevation="0" outlined to="/impugnacoes">
                    <v-icon left small>mdi-arrow-left</v-icon>Voltar
                </v-btn>
                <v-btn id="btnSave" class="details-primary-action" type="button" elevation="0" color="primary" dark @click="save" :loading="dataForm.saveLoading">
                    <v-icon left small>mdi-content-save-outline</v-icon>Salvar alterações
                </v-btn>
            </div>
        </header>

        <v-card v-if="!page.isNew" flat class="proceeding-hero mb-4">
            <v-card-text class="pa-5">
                <div class="d-flex flex-wrap justify-space-between align-start">
                    <div>
                        <div class="process-eyebrow">{{ dataset.data.type | enum(dataset.proceedingTypes, "id", "name") }} · {{ requestKindLabel(dataset.data.request_kind) }}</div>
                        <div class="d-flex align-center flex-wrap" style="gap: 8px">
                            <h1 class="process-heading">{{ dataset.data.process_number }}</h1>
                            <v-btn icon x-small @click="copyProcess"><v-icon small>mdi-content-copy</v-icon></v-btn>
                        </div>
                        <div class="mt-2">
                            <span class="hero-status"><span :style="{ backgroundColor: workflowStatusDotColor(dataset.data.workflow_status) }" />{{ workflowStatusLabel(dataset.data.workflow_status) }}</span>
                        </div>
                        <div class="hero-facts mt-4">
                            <div class="hero-fact" :class="{ missing: !dataset.data.distribution_date }">
                                <span>Data de distribuição</span>
                                <strong>{{ formatShortDate(dataset.data.distribution_date) }}</strong>
                                <button v-if="!dataset.data.distribution_date" type="button" @click="activeSection = 0; scrollTo('identification')">Informar data</button>
                            </div>
                            <div class="hero-fact">
                                <span>Responsável</span>
                                <strong>{{ responsibleName }}</strong>
                            </div>
                            <div class="hero-fact">
                                <span>Natureza do pedido</span>
                                <strong>{{ requestKindLabel(dataset.data.request_kind) }}</strong>
                            </div>
                            <div class="hero-fact">
                                <span>Última atualização</span>
                                <strong>{{ formatDate(dataset.data.updated_at) || '—' }}</strong>
                            </div>
                        </div>
                    </div>
                </div>
            </v-card-text>
        </v-card>

        <v-card v-if="!page.isNew" flat class="section-navigation mb-4">
            <v-tabs v-model="activeSection" background-color="transparent" show-arrows>
                <v-tab>Dados do incidente</v-tab>
                <v-tab>Partes e créditos <span class="tab-count">{{ (dataset.data.participants || []).length }}</span></v-tab>
                <v-tab>Reservas <span class="tab-count">{{ activeReservations.length }}</span></v-tab>
                <v-tab>Andamento processual</v-tab>
                <v-tab>Petições <span class="tab-count">{{ petitionCount }}</span></v-tab>
                <v-tab @click="scrollTo('history')">Histórico</v-tab>
            </v-tabs>
        </v-card>

        <v-form ref="form" v-model="dataForm.validForm">
            <v-row>
                <v-col cols="12">

                    <!-- Card 1: Identificação -->
                    <v-card v-show="page.isNew || activeSection === 0" id="identification" class="card-form-outlined mb-5 section-anchor clean-detail-card">
                        <v-card-title class="card-group-title d-flex justify-space-between">
                            <span>Dados do incidente</span>
                            <div class="d-flex align-center" style="gap: 8px">
                                <span v-if="missingFields.length" class="missing-note"><v-icon small>mdi-alert-outline</v-icon>{{ missingFields.length }} campos pendentes</span>
                            </div>
                        </v-card-title>
                        <v-card-text>
                            <v-row class="mb-4">
                                <v-col cols="12">
                                    <v-btn-toggle
                                        v-model="dataset.data.mode"
                                        mandatory
                                        dense
                                        class="btn-toggle-gap"
                                    >
                                        <v-btn small :value="1">
                                            <v-icon left small>mdi-format-list-text</v-icon>
                                            Detalhado
                                        </v-btn>

                                        <v-btn small :value="2">
                                            <v-icon left small>mdi-text-short</v-icon>
                                            Resumido
                                        </v-btn>
                                    </v-btn-toggle>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="4">
                                    <div class="incident-field"><label>Caso <em>*</em></label><v-autocomplete dense :items="dataset.clients" placeholder="Selecione o caso"
                                        v-model="dataset.data.client_id" item-value="id" item-text="name"
                                        outlined hide-details="auto" required :rules="[$rules.required]"
                                        no-data-text="Nenhum caso encontrado">
                                    </v-autocomplete></div>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <div class="incident-field"><label>Nº do processo <em>*</em></label><v-text-field dense placeholder="Informe o número do processo" v-model="dataset.data.process_number"
                                        required :rules="[$rules.required]" outlined hide-details="auto">
                                    </v-text-field></div>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <div class="incident-field"><label>Tipo <em>*</em></label><v-select dense :items="dataset.proceedingTypes" placeholder="Selecione o tipo"
                                        v-model="dataset.data.type" item-value="id" item-text="name"
                                        outlined hide-details="auto" required :rules="[$rules.required]">
                                    </v-select></div>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="3" :class="{ 'distribution-field-missing': !dataset.data.distribution_date }">
                                    <div class="incident-field"><label>Data de distribuição</label><v-text-field dense type="date" v-model="dataset.data.distribution_date" outlined hide-details="auto" /></div>
                                    <span v-if="!dataset.data.distribution_date" class="field-hint">Informe a data em que o incidente foi distribuído.</span>
                                </v-col>
                                <v-col cols="12" md="3">
                                    <div class="incident-field">
                                        <label>Natureza do pedido <span>Opcional</span></label>
                                        <v-select dense :items="dataset.requestKinds" placeholder="Selecione a natureza" v-model="dataset.data.request_kind" item-value="id" item-text="name" outlined hide-details="auto" clearable :menu-props="{ contentClass: 'proceedings-request-kind-menu', offsetY: true }">
                                            <template v-slot:item="{ item }">
                                                <div class="request-kind-option">
                                                    <span class="request-kind-option-icon"><v-icon size="17">{{ item.icon }}</v-icon></span>
                                                    <span class="request-kind-option-copy"><strong>{{ item.name }}</strong><small>{{ item.description }}</small></span>
                                                </div>
                                            </template>
                                        </v-select>
                                    </div>
                                </v-col>
                                <v-col cols="12" md="3">
                                    <div class="incident-field"><label>Status operacional</label><v-select dense :items="dataset.workflowStatuses" placeholder="Selecione o status" v-model="dataset.data.workflow_status" item-value="id" item-text="name" outlined hide-details="auto" /></div>
                                </v-col>
                                <v-col cols="12" md="3">
                                    <div class="incident-field"><label>Responsável interno <span>Opcional</span></label><v-autocomplete dense :items="dataset.admins" placeholder="Selecione o responsável" v-model="dataset.data.responsible_admin_id" item-value="id" item-text="name" outlined hide-details="auto" clearable /></div>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="3"><div class="incident-field"><label>Vara <span>Opcional</span></label><v-text-field dense placeholder="Informe a vara" v-model="dataset.data.court_division" outlined hide-details="auto" /></div></v-col>
                                <v-col cols="12" md="3"><div class="incident-field"><label>Comarca <span>Opcional</span></label><v-text-field dense placeholder="Informe a comarca" v-model="dataset.data.district" outlined hide-details="auto" /></div></v-col>
                                <v-col cols="12" md="2"><div class="incident-field"><label>UF <span>Opcional</span></label><v-autocomplete dense :items="dataset.statesList" placeholder="Selecione" v-model="dataset.data.state" item-value="name" item-text="name" outlined hide-details="auto" clearable no-data-text="Nenhuma UF encontrada" /></div></v-col>
                                <v-col cols="12" md="4">
                                    <div class="incident-field">
                                        <label>Tribunal <span>Opcional</span></label>
                                        <v-autocomplete dense :items="dataset.tribunalsList" placeholder="Selecione o tribunal" v-model="dataset.data.tribunal" item-value="name" item-text="name" outlined hide-details="auto" clearable no-data-text="Nenhum tribunal encontrado" />
                                    </div>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12"><div class="incident-field"><label>Observações internas <span>Opcional</span></label><v-textarea dense placeholder="Registre informações relevantes para a equipe" v-model="dataset.data.internal_notes" outlined hide-details="auto" rows="3" /></div></v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>

                    <v-card v-show="page.isNew || activeSection === 0" v-if="dataset.data.mode === 2" class="card-form-outlined mb-5 clean-detail-card">
                        <v-card-title class="card-group-title">Resumo da impugnação</v-card-title>
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" md="4">
                                    <strong>Credores:</strong> {{ summaryCreditors }}
                                </v-col>
                                <v-col cols="12" md="4">
                                    <strong>Tipo:</strong> {{ dataset.data.type | enum(dataset.proceedingTypes, "id", "name") }}
                                </v-col>
                                <v-col cols="12" md="4">
                                    <strong>Situação:</strong> {{ dataset.data.status === 1 ? 'Finalizado' : 'Em andamento' }}
                                </v-col>
                                <v-col cols="12" md="4">
                                    <strong>Sentença:</strong> {{ dataset.data.has_sentence ? 'Sim' : 'Não' }}
                                </v-col>
                                <v-col cols="12" md="4">
                                    <strong>Alteração de crédito:</strong> {{ hasCreditChange ? 'Sim' : 'Não' }}
                                </v-col>
                                <v-col cols="12" md="4">
                                    <strong>Trânsito em julgado:</strong> {{ dataset.data.final_judgment ? 'Sim' : 'Não' }}
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>

                    <!-- Card 2: Manifestantes e Créditos -->
                    <v-card v-show="page.isNew || activeSection === 1" id="participants" class="card-form-outlined mb-5 section-anchor clean-detail-card">
                        <v-card-title class="card-group-title participants-title">
                            <div><span>Partes e créditos</span><small>Manifestantes vinculados ao incidente e seus respectivos créditos.</small></div>
                            <v-btn small outlined class="section-primary-action section-compact-action" color="primary" elevation="0" @click="openParticipantDialog">
                                <v-icon left size="15">mdi-plus</v-icon>Adicionar parte
                            </v-btn>
                        </v-card-title>
                        <v-card-text>
                            <div v-if="dataset.data.participants && dataset.data.participants.length" class="participants-stack">
                                <article v-for="(participant, pIndex) in dataset.data.participants" :key="'participant-' + (participant.id || participant._tempId || pIndex)" class="participant-card" :class="{ collapsed: !isParticipantExpanded(participant, pIndex) }">
                                    <header class="participant-card-header">
                                        <div class="participant-identity">
                                            <span class="participant-avatar">{{ participantInitials(participant.name) }}</span>
                                            <div>
                                                <div class="participant-name-row">
                                                    <strong>{{ participant.name || 'Nome não informado' }}</strong>
                                                    <span class="role-label">{{ participant.role | enum(dataset.participantRoles, "id", "name") }}</span>
                                                </div>
                                                <div class="participant-meta">
                                                    <span>{{ formatRegistration(participant.registration) }}</span>
                                                    <span v-if="participant.position">{{ getParticipantPositionLabel(participant.position) }}</span>
                                                </div>
                                                <div v-if="!isParticipantExpanded(participant, pIndex) && participant.role === 1" class="participant-collapse-summary">
                                                    <span><strong>{{ participant.qgc_claims ? participant.qgc_claims.length : 0 }}</strong> {{ participant.qgc_claims && participant.qgc_claims.length === 1 ? 'crédito no QGC' : 'créditos no QGC' }}</span>
                                                    <span><strong>{{ participant.credits ? participant.credits.length : 0 }}</strong> {{ participant.credits && participant.credits.length === 1 ? 'crédito discutido' : 'créditos discutidos' }}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="participant-actions">
                                            <template v-if="isParticipantExpanded(participant, pIndex)">
                                                <v-btn v-if="participant.suggested_registration" small outlined color="primary" @click="acceptParticipantSuggestion(participant)">Confirmar CPF</v-btn>
                                                <v-btn v-if="participant.role === 1" small outlined color="primary" @click="openCreditDialog(participant, pIndex)"><v-icon left small>mdi-plus</v-icon>Adicionar crédito</v-btn>
                                                <v-btn v-if="participant.role === 1" icon small aria-label="Editar credor" title="Editar credor" @click="openParticipantCorrection(participant)"><v-icon size="18">mdi-pencil-outline</v-icon></v-btn>
                                                <v-btn icon small aria-label="Excluir parte" title="Excluir parte" @click="removeParticipant(participant, pIndex)"><v-icon size="18" color="error">mdi-trash-can-outline</v-icon></v-btn>
                                            </template>
                                            <v-btn icon small class="participant-expand-button" :aria-label="isParticipantExpanded(participant, pIndex) ? 'Recolher credor' : 'Expandir credor'" :title="isParticipantExpanded(participant, pIndex) ? 'Recolher credor' : 'Expandir credor'" :aria-expanded="isParticipantExpanded(participant, pIndex)" @click="toggleParticipantExpansion(participant, pIndex)">
                                                <v-icon size="20">{{ isParticipantExpanded(participant, pIndex) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                            </v-btn>
                                        </div>
                                    </header>

                                    <v-expand-transition>
                                        <div v-show="isParticipantExpanded(participant, pIndex)" class="participant-card-details">
                                            <div v-if="participant.suggested_registration || participant.qgc_match_reason" class="participant-notice">
                                                <v-icon small>mdi-alert-circle-outline</v-icon>
                                                <span><strong v-if="participant.suggested_registration">CPF/CNPJ sugerido: {{ formatRegistration(participant.suggested_registration) }}.</strong> {{ participant.qgc_match_reason }}</span>
                                            </div>

                                            <div v-if="participant.role === 1" class="participant-credit-groups">
                                                <section class="credit-group">
                                                    <div class="credit-group-heading"><span>Crédito no QGC</span><small>Referência do quadro geral de credores</small></div>
                                                    <div v-if="participant.qgc_claims && participant.qgc_claims.length" class="credit-rows">
                                                        <div v-for="claim in participant.qgc_claims" :key="'qgc-' + claim.id" class="credit-result-row">
                                                            <span class="credit-source">QGC v{{ claim.version || 3 }}</span>
                                                            <div class="credit-main"><strong>{{ formatClaimAmount(claim) }}</strong><span>{{ claim.type | enum(dataset.creditorTypes, "id", "name") }}</span></div>
                                                            <span class="credit-detail">{{ claim.currency | enum(dataset.currencyTypes, "id", "name") }}</span>
                                                        </div>
                                                    </div>
                                                    <div v-else class="credit-empty"><v-icon small>mdi-information-outline</v-icon>Nenhum crédito encontrado no QGC para este CPF/CNPJ.</div>
                                                </section>

                                                <section class="credit-group">
                                                    <div class="credit-group-heading"><span>Créditos discutidos</span><small>Valores vinculados a esta impugnação</small></div>
                                                    <div v-if="participant.credits && participant.credits.length" class="credit-rows">
                                                        <div v-for="(credit, cIndex) in participant.credits" :key="'credit-' + (credit.id || credit._tempId || cIndex)" class="credit-result-row">
                                                            <span class="credit-source discussed">Incidente</span>
                                                            <div class="credit-main"><strong>{{ formatAmount(credit.value, credit.currency) }}</strong><span>{{ credit.class | enum(dataset.creditorTypes, "id", "name") }}</span></div>
                                                            <div class="credit-detail"><span v-if="credit.pretension">{{ credit.pretension | enum(dataset.pretensionTypes, "id", "name") }}</span><span v-if="credit.origin_identifier">{{ credit.origin_identifier }}</span></div>
                                                            <div class="credit-row-actions">
                                                                <v-btn icon small aria-label="Editar crédito" title="Editar crédito" @click="openCreditDialog(participant, pIndex, credit, cIndex)"><v-icon size="17">mdi-pencil-outline</v-icon></v-btn>
                                                                <v-btn icon small aria-label="Excluir crédito" title="Excluir crédito" @click="removeCredit(participant, pIndex, credit, cIndex)"><v-icon size="17" color="error">mdi-trash-can-outline</v-icon></v-btn>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button v-else type="button" class="credit-empty actionable" @click="openCreditDialog(participant, pIndex)"><v-icon small>mdi-plus-circle-outline</v-icon>Adicionar o primeiro crédito discutido</button>
                                                </section>
                                            </div>
                                        </div>
                                    </v-expand-transition>
                                </article>
                            </div>
                            <div v-else class="empty-section"><v-icon size="34">mdi-account-multiple-outline</v-icon><strong>Nenhuma parte adicionada</strong><span>Inclua o primeiro manifestante relacionado à impugnação.</span></div>
                        </v-card-text>
                    </v-card>

                    <v-card v-show="activeSection === 2" id="reservations" v-if="!page.isNew" class="card-form-outlined mb-5 section-anchor clean-detail-card">
                        <v-card-title class="card-group-title participants-title reservation-title">
                            <div>
                                <span>Reservas de crédito</span>
                                <small class="reservation-help">Valores provisórios; não alteram o crédito definitivo no QGC nem produzem efeitos automáticos em votação ou adesão.</small>
                            </div>
                            <v-btn v-if="canCreateReservation" small outlined class="section-primary-action section-compact-action" color="primary" elevation="0" @click="openReservationDialog()">
                                <v-icon left size="15">mdi-plus</v-icon>Criar reserva
                            </v-btn>
                        </v-card-title>
                        <v-card-text>
                            <div v-if="reservationSummary.length" class="reservation-summary mb-4">
                                <div v-for="item in reservationSummary" :key="item.currency"><span>Reservado ({{ item.currency }})</span><strong>{{ item.formatted }}</strong></div>
                            </div>
                            <v-data-table :headers="reservationHeaders" :items="dataset.data.reservations || []" disable-pagination hide-default-footer dense no-data-text="Nenhuma reserva cadastrada">
                                <template v-slot:no-data>
                                    <div class="empty-section">
                                        <v-icon size="34" color="#a4a8b6">mdi-bank-outline</v-icon>
                                        <strong>Nenhuma reserva cadastrada</strong>
                                        <span>Registre um valor provisório relacionado a esta impugnação.</span>
                                        <v-btn v-if="canCreateReservation" small outlined color="primary" @click="openReservationDialog()">Criar primeira reserva</v-btn>
                                    </div>
                                </template>
                                <template v-slot:[`item.participant`]="{ item }">{{ item.participant ? item.participant.name : getParticipantName(item.participant_id) }}</template>
                                <template v-slot:[`item.amount`]="{ item }"><strong>{{ formatAmount(item.amount, item.currency) }}</strong></template>
                                <template v-slot:[`item.class`]="{ item }">{{ item.class | enum(dataset.creditorTypes, "id", "name") }}</template>
                                <template v-slot:[`item.status`]="{ item }"><v-chip x-small :color="item.status === 'active' ? '#e5f5eb' : '#edf0f4'">{{ item.status === 'active' ? 'Ativa' : 'Cancelada' }}</v-chip></template>
                                <template v-slot:[`item.created_at`]="{ item }">{{ formatDate(item.created_at) }}</template>
                                <template v-slot:[`item.actions`]="{ item }">
                                    <div class="reservation-row-actions">
                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn v-if="canUpdateReservation" v-bind="attrs" v-on="on" icon small aria-label="Editar reserva" @click="openReservationDialog(item)" :disabled="item.status !== 'active'"><v-icon small color="primary">mdi-pencil-outline</v-icon></v-btn>
                                            </template>
                                            <span>Editar reserva</span>
                                        </v-tooltip>
                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn v-if="canCancelReservation" v-bind="attrs" v-on="on" icon small aria-label="Cancelar reserva" @click="openCancelReservation(item)" :disabled="item.status !== 'active'"><v-icon small color="error">mdi-cancel</v-icon></v-btn>
                                            </template>
                                            <span>Cancelar reserva</span>
                                        </v-tooltip>
                                    </div>
                                </template>
                            </v-data-table>
                        </v-card-text>
                    </v-card>

                    <div v-show="activeSection === 3" class="process-workspace">
                        <header class="process-section-intro">
                            <div>
                                <span class="process-section-eyebrow">Fluxo do incidente</span>
                                <h2>Andamento processual</h2>
                                <p>Registre manifestações, decisões e os efeitos da sentença ou dos recursos sobre os créditos.</p>
                            </div>
                            <div class="process-state" :class="{ completed: dataset.data.final_judgment }">
                                <v-icon small>{{ dataset.data.final_judgment ? 'mdi-check-circle-outline' : 'mdi-progress-clock' }}</v-icon>
                                <span>{{ dataset.data.final_judgment ? 'Trânsito em julgado' : 'Processo em andamento' }}</span>
                            </div>
                        </header>

                        <v-card v-if="dataset.data.mode === 1" id="documents" class="process-panel section-anchor" :class="{ collapsed: !isProcessBlockExpanded('manifestations') }" flat>
                            <div class="process-panel-header">
                                <div class="process-panel-title">
                                    <span class="process-panel-icon"><v-icon small>mdi-file-document-edit-outline</v-icon></span>
                                    <div><h3>Manifestações</h3><p>Documentos e posicionamentos apresentados pelas partes.</p></div>
                                </div>
                                <div class="process-header-actions">
                                    <span class="process-block-count">{{ (dataset.data.manifestations || []).length }}</span>
                                    <v-btn v-if="isProcessBlockExpanded('manifestations')" color="primary" elevation="0" small @click="openManifestationDialog"><v-icon left small>mdi-plus</v-icon>Adicionar manifestação</v-btn>
                                    <v-btn icon small class="process-expand-button" :aria-label="isProcessBlockExpanded('manifestations') ? 'Recolher manifestações' : 'Expandir manifestações'" @click="toggleProcessBlock('manifestations')"><v-icon size="20">{{ isProcessBlockExpanded('manifestations') ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon></v-btn>
                                </div>
                            </div>
                            <v-expand-transition>
                                <div v-show="isProcessBlockExpanded('manifestations')">
                                    <div v-if="dataset.data.manifestations && dataset.data.manifestations.length" class="process-record-list">
                                        <article v-for="(manifestation, mIndex) in dataset.data.manifestations" :key="'m-' + mIndex" class="process-record">
                                            <span class="process-record-icon"><v-icon small>mdi-file-document-outline</v-icon></span>
                                            <div class="process-record-content">
                                                <div class="process-record-title"><strong>{{ getParticipantName(manifestation.participant_id) }}</strong><span v-if="manifestation.reference || manifestation.ref" class="process-reference">REF {{ manifestation.reference || manifestation.ref }}</span></div>
                                                <p>{{ manifestation.description || 'Manifestação sem descrição.' }}</p>
                                            </div>
                                            <v-btn icon small aria-label="Excluir manifestação" @click="removeManifestation(manifestation, mIndex)"><v-icon size="17" color="#dc2626">mdi-delete-outline</v-icon></v-btn>
                                        </article>
                                    </div>
                                    <div v-else class="process-empty-state"><span><v-icon>mdi-file-document-plus-outline</v-icon></span><div><strong>Nenhuma manifestação registrada</strong><p>Adicione o primeiro posicionamento apresentado no incidente.</p></div></div>
                                </div>
                            </v-expand-transition>
                        </v-card>

                        <div class="process-stage-grid">
                            <v-card class="process-stage-card" :class="{ active: dataset.data.has_sentence, collapsed: !isProcessBlockExpanded('sentence') }" flat>
                                <div class="process-stage-header">
                                    <div class="process-panel-title"><span class="process-panel-icon"><v-icon small>mdi-gavel</v-icon></span><div><h3>Sentença</h3><p>Decisão principal do incidente.</p></div></div>
                                    <div class="process-header-actions"><div class="process-stage-toggle"><span>{{ dataset.data.has_sentence ? 'Registrada' : 'Não registrada' }}</span><v-switch v-model="dataset.data.has_sentence" hide-details class="ma-0" aria-label="Houve sentença?"></v-switch></div><v-btn icon small class="process-expand-button" :aria-label="isProcessBlockExpanded('sentence') ? 'Recolher sentença' : 'Expandir sentença'" @click="toggleProcessBlock('sentence')"><v-icon size="20">{{ isProcessBlockExpanded('sentence') ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon></v-btn></div>
                                </div>
                                <v-expand-transition>
                                    <div v-show="isProcessBlockExpanded('sentence')">
                                        <template v-if="dataset.data.has_sentence">
                                            <div class="process-stage-body"><div class="dialog-field-group"><label>Resultado da sentença</label><v-textarea dense placeholder="Descreva o resultado da sentença" v-model="dataset.data.sentence_result" outlined hide-details="auto" rows="3"></v-textarea></div></div>
                                            <div class="process-linked-section">
                                                <div class="process-linked-header"><div><strong>Créditos vinculados</strong><span>{{ stageCreditResults('sentence').length }} {{ stageCreditResults('sentence').length === 1 ? 'crédito' : 'créditos' }}</span></div><v-btn outlined color="primary" small @click="openCreditResultDialog('sentence')"><v-icon left small>mdi-plus</v-icon>Adicionar</v-btn></div>
                                                <div v-if="stageCreditResults('sentence').length" class="process-credit-list">
                                                    <article v-for="result in stageCreditResults('sentence')" :key="'sentence-result-' + (result.id || result._tempId)" class="process-credit-item"><div><strong>{{ getParticipantName(result.participant_id) }}</strong><span>{{ getCreditLabel(result) }}</span><small v-if="result.final_class">Classe final: {{ result.final_class | enum(dataset.creditorTypes, "id", "name") }}</small><small v-if="result.result_text">{{ result.result_text }}</small></div><span v-if="result.final_value" class="process-credit-value">{{ formatCurrency(result.final_value) }}</span><v-btn icon x-small aria-label="Excluir crédito da sentença" @click="removeCreditResult(result)"><v-icon size="16" color="#dc2626">mdi-delete-outline</v-icon></v-btn></article>
                                                </div>
                                                <div v-else class="process-inline-empty">Nenhum crédito vinculado à sentença.</div>
                                            </div>
                                        </template>
                                        <div v-else class="process-stage-empty"><v-icon>mdi-gavel</v-icon><span>Ative esta etapa quando houver uma sentença.</span></div>
                                    </div>
                                </v-expand-transition>
                            </v-card>

                            <v-card class="process-stage-card" :class="{ active: dataset.data.has_appeal, collapsed: !isProcessBlockExpanded('appeal') }" flat>
                                <div class="process-stage-header">
                                    <div class="process-panel-title"><span class="process-panel-icon"><v-icon small>mdi-source-branch</v-icon></span><div><h3>Recursos</h3><p>Desdobramentos posteriores à sentença.</p></div></div>
                                    <div class="process-header-actions"><div class="process-stage-toggle"><span>{{ dataset.data.has_appeal ? 'Registrado' : 'Não registrado' }}</span><v-switch v-model="dataset.data.has_appeal" hide-details class="ma-0" aria-label="Houve recurso?"></v-switch></div><v-btn icon small class="process-expand-button" :aria-label="isProcessBlockExpanded('appeal') ? 'Recolher recursos' : 'Expandir recursos'" @click="toggleProcessBlock('appeal')"><v-icon size="20">{{ isProcessBlockExpanded('appeal') ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon></v-btn></div>
                                </div>
                                <v-expand-transition>
                                    <div v-show="isProcessBlockExpanded('appeal')">
                                        <template v-if="dataset.data.has_appeal">
                                            <div class="process-stage-body"><div class="dialog-field-group"><label>Resultado do recurso</label><v-select dense :items="dataset.appealResults" placeholder="Selecione o resultado" v-model="dataset.data.appeal_result" item-value="id" item-text="name" outlined hide-details="auto"></v-select></div></div>
                                            <div class="process-linked-section">
                                                <div class="process-linked-header"><div><strong>Créditos vinculados</strong><span>{{ stageCreditResults('appeal').length }} {{ stageCreditResults('appeal').length === 1 ? 'crédito' : 'créditos' }}</span></div><v-btn outlined color="primary" small @click="openCreditResultDialog('appeal')"><v-icon left small>mdi-plus</v-icon>Adicionar</v-btn></div>
                                                <div v-if="stageCreditResults('appeal').length" class="process-credit-list">
                                                    <article v-for="result in stageCreditResults('appeal')" :key="'appeal-result-' + (result.id || result._tempId)" class="process-credit-item"><div><strong>{{ getParticipantName(result.participant_id) }}</strong><span>{{ getCreditLabel(result) }}</span><small v-if="result.final_class">Classe final: {{ result.final_class | enum(dataset.creditorTypes, "id", "name") }}</small><small v-if="result.result_text">{{ result.result_text }}</small></div><span v-if="result.final_value" class="process-credit-value">{{ formatCurrency(result.final_value) }}</span><v-btn icon x-small aria-label="Excluir crédito do recurso" @click="removeCreditResult(result)"><v-icon size="16" color="#dc2626">mdi-delete-outline</v-icon></v-btn></article>
                                                </div>
                                                <div v-else class="process-inline-empty">Nenhum crédito vinculado ao recurso.</div>
                                            </div>
                                        </template>
                                        <div v-else class="process-stage-empty"><v-icon>mdi-source-branch</v-icon><span>Ative esta etapa quando houver um recurso.</span></div>
                                    </div>
                                </v-expand-transition>
                            </v-card>
                        </div>

                        <div v-if="!page.isNew && hasCreditChange" class="qgc-action-banner">
                            <div><span><v-icon small>mdi-database-sync-outline</v-icon></span><div><strong>Alteração de crédito identificada</strong><p>Revise os resultados processuais antes de refletir os valores no QGC.</p></div></div>
                            <v-btn color="primary" elevation="0" small @click="applyQgc" :loading="dataForm.applyQgcLoading" :disabled="!!dataset.data.qgc_updated"><v-icon left small>{{ dataset.data.qgc_updated ? 'mdi-check-circle' : 'mdi-check-circle-outline' }}</v-icon>{{ dataset.data.qgc_updated ? 'QGC atualizado' : 'Autorizar alteração QGC' }}</v-btn>
                        </div>

                        <v-card v-if="dataset.data.mode === 1" class="process-panel" :class="{ collapsed: !isProcessBlockExpanded('decisions') }" flat>
                            <div class="process-panel-header">
                                <div class="process-panel-title"><span class="process-panel-icon"><v-icon small>mdi-file-sign</v-icon></span><div><h3>Decisões intermediárias</h3><p>Despachos e decisões proferidas durante o andamento.</p></div></div>
                                <div class="process-header-actions"><span class="process-block-count">{{ (dataset.data.decisions || []).length }}</span><v-btn v-if="isProcessBlockExpanded('decisions')" color="primary" elevation="0" small @click="openDecisionDialog"><v-icon left small>mdi-plus</v-icon>Adicionar decisão</v-btn><v-btn icon small class="process-expand-button" :aria-label="isProcessBlockExpanded('decisions') ? 'Recolher decisões' : 'Expandir decisões'" @click="toggleProcessBlock('decisions')"><v-icon size="20">{{ isProcessBlockExpanded('decisions') ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon></v-btn></div>
                            </div>
                            <v-expand-transition>
                                <div v-show="isProcessBlockExpanded('decisions')">
                                    <div v-if="dataset.data.decisions && dataset.data.decisions.length" class="process-record-list">
                                        <article v-for="(decision, dIndex) in dataset.data.decisions" :key="'decision-' + dIndex" class="process-record">
                                            <span class="process-record-icon"><v-icon small>mdi-file-document-alert-outline</v-icon></span>
                                            <div class="process-record-content"><div class="process-record-title"><strong>{{ decision.title }}</strong><span class="process-reference">{{ decision.decision_type | enum(dataset.decisionTypes, "id", "name") }}</span></div><p><span v-if="decision.reference">Ref. {{ decision.reference }}</span><span v-if="decision.decided_at"> · {{ decision.decided_at | defaultDate("DD/MM/YYYY") }}</span><span v-if="decision.is_visible_to_creditor"> · Visível ao credor</span></p></div>
                                            <div class="process-record-actions"><v-btn v-if="decision.file_url" icon small :href="decision.file_url" target="_blank" rel="noopener noreferrer" aria-label="Visualizar decisão"><v-icon size="17" color="primary">mdi-open-in-new</v-icon></v-btn><v-btn icon small aria-label="Excluir decisão" @click="removeDecision(decision, dIndex)"><v-icon size="17" color="#dc2626">mdi-delete-outline</v-icon></v-btn></div>
                                        </article>
                                    </div>
                                    <div v-else class="process-empty-state"><span><v-icon>mdi-file-sign</v-icon></span><div><strong>Nenhuma decisão intermediária</strong><p>Inclua despachos e decisões relevantes para o acompanhamento.</p></div></div>
                                </div>
                            </v-expand-transition>
                        </v-card>

                        <v-card class="final-judgment-card" :class="{ completed: dataset.data.final_judgment }" flat>
                            <div><span class="process-panel-icon"><v-icon small>{{ dataset.data.final_judgment ? 'mdi-check-decagram-outline' : 'mdi-timeline-clock-outline' }}</v-icon></span><div><h3>Encerramento processual</h3><p>{{ dataset.data.final_judgment ? 'O trânsito em julgado está registrado para este incidente.' : 'Marque quando não houver mais recursos cabíveis.' }}</p></div></div>
                            <div class="process-stage-toggle"><span>Trânsito em julgado</span><v-switch v-model="dataset.data.final_judgment" hide-details class="ma-0" aria-label="Trânsito em julgado?"></v-switch></div>
                        </v-card>
                    </div>

                    <ProceedingPetitions v-if="!page.isNew" v-show="activeSection === 4" :proceeding-id="$route.params.id" @count="petitionCount = $event" />

                    <!-- Card 7: Comentários internos -->
                    <v-card v-show="activeSection === 5" id="history" class="card-form-outlined mb-5 section-anchor clean-detail-card history-card">
                        <v-card-title class="card-group-title history-card-title">
                            <div>
                                <span>Histórico da impugnação</span>
                                <small>Acompanhe as alterações do processo e as observações registradas pela equipe.</small>
                            </div>
                            <div class="history-summary">
                                <span><strong>{{ (dataset.data.audits || []).length }}</strong> atividades</span>
                                <span><strong>{{ (dataset.data.comments || []).length }}</strong> comentários</span>
                            </div>
                        </v-card-title>
                        <v-card-text class="history-card-body">
                            <div class="history-layout">
                                <section class="history-panel activity-panel">
                                    <header class="history-panel-header">
                                        <span class="history-panel-icon"><v-icon size="18">mdi-history</v-icon></span>
                                        <div><strong>Linha do tempo</strong><small>Registro automático das atividades realizadas.</small></div>
                                    </header>

                                    <div v-if="dataset.data.audits && dataset.data.audits.length" class="activity-feed">
                                        <article v-for="audit in dataset.data.audits" :key="audit.id" class="activity-entry">
                                            <span class="activity-marker"><v-icon size="15">{{ auditIcon(audit.action) }}</v-icon></span>
                                            <div class="activity-entry-content">
                                                <strong>{{ audit.description || audit.action }}</strong>
                                                <div class="activity-meta">
                                                    <span><v-icon size="13">mdi-calendar-blank-outline</v-icon>{{ formatDate(audit.created_at) }}</span>
                                                    <span><v-icon size="13">mdi-account-outline</v-icon>{{ audit.admin ? audit.admin.name : 'Sistema' }}</span>
                                                </div>
                                            </div>
                                        </article>
                                    </div>
                                    <div v-else class="history-empty-state">
                                        <v-icon size="24">mdi-history</v-icon><strong>Nenhuma atividade registrada</strong><span>As alterações realizadas aparecerão nesta linha do tempo.</span>
                                    </div>
                                </section>

                                <section class="history-panel comments-panel">
                                    <header class="history-panel-header comments-header">
                                        <span class="history-panel-icon"><v-icon size="18">mdi-message-text-outline</v-icon></span>
                                        <div><strong>Comentários internos</strong><small>Visíveis somente para a equipe administrativa.</small></div>
                                    </header>

                                    <div v-if="dataset.data.comments && dataset.data.comments.length" class="comments-feed">
                                        <article v-for="(comment, cIdx) in dataset.data.comments" :key="'comment-' + cIdx" class="comment-entry">
                                            <span class="comment-avatar">{{ participantInitials(comment.admin ? comment.admin.name : 'Admin') }}</span>
                                            <div class="comment-content">
                                                <div class="comment-author"><strong>{{ comment.admin ? comment.admin.name : 'Admin' }}</strong><time>{{ formatDate(comment.created_at) }}</time></div>
                                                <p>{{ comment.content }}</p>
                                            </div>
                                        </article>
                                    </div>
                                    <div v-else class="history-empty-state compact">
                                        <v-icon size="23">mdi-message-outline</v-icon><strong>Nenhum comentário</strong><span>Registre abaixo uma observação para a equipe.</span>
                                    </div>

                                    <div v-if="!page.isNew" class="comment-composer">
                                        <label for="new-history-comment">Adicionar comentário</label>
                                        <v-textarea id="new-history-comment" v-model="newComment" outlined dense hide-details="auto" rows="3" auto-grow placeholder="Escreva uma observação interna..." />
                                        <div class="comment-composer-footer">
                                            <span><v-icon size="13">mdi-lock-outline</v-icon>Uso interno</span>
                                            <v-btn color="primary" elevation="0" small @click="addComment" :loading="dataForm.commentLoading" :disabled="!newComment || !newComment.trim()">
                                                <v-icon left size="15">mdi-send-outline</v-icon>Comentar
                                            </v-btn>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </v-card-text>
                    </v-card>

                </v-col>
            </v-row>
        </v-form>

        <!-- Dialog: Adicionar manifestante -->
        <v-dialog v-model="dialogParticipant" max-width="600px" content-class="proceedings-shadcn-dialog participant-dialog" scrollable persistent>
            <v-card>
                <v-card-title class="dialog-header">
                    <div><span>Adicionar manifestante</span><small>Vincule um credor existente no QGC ou cadastre uma nova parte.</small></div>
                    <v-btn icon small aria-label="Fechar" @click="dialogParticipant = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <v-form ref="participantForm" v-model="dataForm.participantFormValid" class="participant-dialog-form">
                        <div class="dialog-fields-grid">
                            <div class="dialog-field-group">
                                <label>Papel no incidente</label>
                                <v-select dense :items="dataset.participantRoles" placeholder="Selecione o papel"
                                    v-model="participantForm.role" item-value="id" item-text="name"
                                    outlined hide-details="auto" :rules="[$rules.required]">
                                </v-select>
                            </div>
                            <div class="dialog-field-group">
                                <label>Posição processual</label>
                                <v-select dense :items="dataset.participantPositions"
                                    placeholder="Selecione a posição" v-model="participantForm.position" item-value="id"
                                    item-text="name" outlined hide-details="auto" clearable>
                                </v-select>
                            </div>
                        </div>

                        <div class="dialog-section">
                            <span class="dialog-field-label">Origem do cadastro</span>
                            <div class="origin-selector">
                                <button type="button" class="origin-option" :class="{ active: participantForm.source === 'qgc' }" @click="participantForm.source = 'qgc'">
                                    <span class="origin-option-icon"><v-icon small>mdi-database-search-outline</v-icon></span>
                                    <span><strong>Credor do QGC</strong><small>Localize e vincule um credor já cadastrado.</small></span>
                                    <v-icon class="origin-check" small>{{ participantForm.source === 'qgc' ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                                </button>
                                <button type="button" class="origin-option" :class="{ active: participantForm.source === 'new' }" @click="participantForm.source = 'new'">
                                    <span class="origin-option-icon"><v-icon small>mdi-account-plus-outline</v-icon></span>
                                    <span><strong>Novo manifestante</strong><small>Cadastre uma parte ainda não vinculada ao QGC.</small></span>
                                    <v-icon class="origin-check" small>{{ participantForm.source === 'new' ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                                </button>
                            </div>
                        </div>

                        <template v-if="participantForm.source === 'qgc'">
                            <div class="dialog-section dialog-field-group">
                                <label>Credor</label>
                                    <v-autocomplete dense :items="dataset.userCreditors" placeholder="Busque por nome ou CPF/CNPJ"
                                        v-model="participantForm.user_creditor_id" item-value="id" item-text="name"
                                        outlined hide-details="auto" :rules="[$rules.required]"
                                        no-data-text="Nenhum credor encontrado"
                                        :loading="dataForm.userCreditorsLoading"
                                        @update:search-input="searchUserCreditors">
                                    </v-autocomplete>
                                <small class="dialog-field-help">Digite ao menos dois caracteres para pesquisar no QGC.</small>
                            </div>
                        </template>
                        <template v-if="participantForm.source === 'new'">
                            <div class="dialog-section dialog-fields-grid">
                                <div class="dialog-field-group">
                                    <label>Nome completo</label>
                                    <v-text-field dense placeholder="Informe o nome" v-model="participantForm.name"
                                        outlined hide-details="auto" :rules="[$rules.required]">
                                    </v-text-field>
                                </div>
                                <div class="dialog-field-group">
                                    <label>CPF/CNPJ</label>
                                    <v-text-field dense placeholder="Informe o documento" v-model="participantForm.registration"
                                        outlined hide-details="auto">
                                    </v-text-field>
                                </div>
                            </div>
                        </template>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="dialogParticipant = false">Cancelar</v-btn>
                    <v-btn color="primary" elevation="0" @click="confirmAddParticipant"
                        :loading="dataForm.participantLoading">Adicionar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog: Corrigir vínculo QGC -->
        <v-dialog v-model="dialogParticipantCorrection" max-width="520px" content-class="proceedings-shadcn-dialog" persistent>
            <v-card>
                <v-card-title class="dialog-header">
                    <div><span>Corrigir manifestante</span><small>Atualize os dados usados para identificar o credor.</small></div>
                    <v-btn icon small aria-label="Fechar" @click="dialogParticipantCorrection = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col cols="12">
                            <v-text-field dense label="Nome do credor" v-model="participantCorrectionForm.name"
                                outlined hide-details="auto">
                            </v-text-field>
                        </v-col>
                        <v-col cols="12">
                            <v-text-field dense label="CPF/CNPJ" v-model="participantCorrectionForm.registration"
                                outlined hide-details="auto">
                            </v-text-field>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="dialogParticipantCorrection = false">Cancelar</v-btn>
                    <v-btn color="primary" elevation="0" @click="saveParticipantCorrection"
                        :loading="dataForm.participantLoading">Salvar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog: Adicionar ou editar crédito -->
        <v-dialog v-model="dialogCredit" max-width="600px" content-class="proceedings-shadcn-dialog credit-dialog" scrollable persistent>
            <v-card>
                <v-card-title class="dialog-header">
                    <div><span>{{ creditEditing ? 'Editar crédito' : 'Adicionar crédito' }}</span><small>Informe o valor, a classe e a origem do crédito relacionado.</small></div>
                    <v-btn icon small aria-label="Fechar" @click="dialogCredit = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <v-form ref="creditForm" v-model="dataForm.creditFormValid" class="credit-dialog-form">
                        <section class="credit-form-section">
                            <div class="credit-section-heading">
                                <strong>Dados do crédito</strong>
                                <span>Informe o valor e a classificação utilizada no incidente.</span>
                            </div>
                            <div class="dialog-fields-grid credit-value-grid">
                                <div class="dialog-field-group">
                                    <label>Valor <em>*</em></label>
                                    <v-currency-field dense placeholder="0,00" v-model="creditForm.value"
                                        :allow-negative="false" outlined hide-details="auto" :rules="[$rules.required]">
                                    </v-currency-field>
                                </div>
                                <div class="dialog-field-group">
                                    <label>Moeda <em>*</em></label>
                                    <v-select dense :items="dataset.currencyTypes" placeholder="Selecione a moeda"
                                        v-model="creditForm.currency" item-value="id" item-text="name"
                                        outlined hide-details="auto" :rules="[$rules.required]">
                                    </v-select>
                                </div>
                                <div class="dialog-field-group">
                                    <label>Classe <em>*</em></label>
                                    <v-select dense :items="dataset.creditorTypes" placeholder="Selecione a classe"
                                        v-model="creditForm.creditor_class" item-value="id" item-text="name"
                                        outlined hide-details="auto" :rules="[$rules.required]">
                                    </v-select>
                                </div>
                                <div class="dialog-field-group">
                                    <label>Pretensão <span>Opcional</span></label>
                                    <v-select dense :items="dataset.pretensionTypes" placeholder="Selecione a pretensão"
                                        v-model="creditForm.pretension" item-value="id" item-text="name"
                                        outlined hide-details="auto" clearable>
                                    </v-select>
                                </div>
                            </div>
                        </section>

                        <section class="credit-form-section">
                            <div class="credit-section-heading">
                                <strong>Origem do crédito</strong>
                                <span>Registre a referência que permite identificar o crédito.</span>
                            </div>
                            <div class="dialog-field-group">
                                <label>Nº do pedido ou identificador</label>
                                <v-text-field dense placeholder="Informe o número ou a referência"
                                    v-model="creditForm.origin_identifier" outlined hide-details="auto">
                                </v-text-field>
                            </div>
                            <div class="dialog-field-group">
                                <label>Descrição da origem</label>
                                <v-combobox dense :items="dataset.creditorOriginTypes"
                                    placeholder="Selecione ou informe uma nova origem"
                                    v-model="creditForm.origin_description" item-value="name" item-text="name"
                                    :return-object="false" outlined hide-details="auto" clearable>
                                    <template v-slot:no-data>
                                        <v-list-item>
                                            <v-list-item-content>
                                                <v-list-item-title>
                                                    Nenhuma origem encontrada. Pressione Enter para adicionar o texto digitado.
                                                </v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </template>
                                </v-combobox>
                            </div>
                        </section>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="dialogCredit = false">Cancelar</v-btn>
                    <v-btn color="primary" elevation="0" @click="confirmCredit"
                        :loading="dataForm.creditLoading">{{ creditEditing ? 'Salvar alterações' : 'Adicionar' }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog: Adicionar manifestação -->
        <v-dialog v-model="dialogManifestation" max-width="650px" content-class="proceedings-shadcn-dialog manifestation-dialog" scrollable persistent>
            <v-card>
                <v-card-title class="dialog-header">
                    <div><span>Adicionar manifestação</span><small>Registre a manifestação e, se necessário, os dados do crédito discutido.</small></div>
                    <v-btn icon small aria-label="Fechar" @click="dialogManifestation = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <v-form ref="manifestationForm" v-model="dataForm.manifestationFormValid" class="manifestation-dialog-form">
                        <section class="manifestation-form-section">
                            <div class="manifestation-section-heading">
                                <span class="manifestation-section-icon"><v-icon small>mdi-account-voice</v-icon></span>
                                <div><strong>Dados da manifestação</strong><small>Identifique a parte e registre o conteúdo apresentado.</small></div>
                            </div>
                            <div class="dialog-fields-grid manifestation-identification-grid">
                                <div class="dialog-field-group">
                                    <label>Manifestante <em>*</em></label>
                                    <v-select dense :items="participantSelectItems" placeholder="Selecione a parte"
                                        v-model="manifestationForm.participant_id" item-value="id" item-text="name"
                                        outlined hide-details="auto" :rules="[$rules.required]">
                                    </v-select>
                                </div>
                                <div class="dialog-field-group">
                                    <label>Referência <span>Opcional</span></label>
                                    <v-text-field dense placeholder="Número, ID ou referência" v-model="manifestationForm.ref"
                                        outlined hide-details="auto">
                                    </v-text-field>
                                </div>
                            </div>
                            <div class="dialog-field-group">
                                <label>Descrição <em>*</em></label>
                                <v-textarea dense placeholder="Descreva o conteúdo da manifestação"
                                    v-model="manifestationForm.description" outlined hide-details="auto" rows="3" :rules="[$rules.required]">
                                </v-textarea>
                            </div>
                        </section>

                        <section class="manifestation-form-section manifestation-credit-section">
                            <div class="manifestation-section-heading">
                                <span class="manifestation-section-icon"><v-icon small>mdi-cash-multiple</v-icon></span>
                                <div><strong>Crédito informado</strong><small>Preencha somente quando a manifestação mencionar um valor específico.</small></div>
                                <span class="manifestation-optional-badge">Opcional</span>
                            </div>
                            <div class="manifestation-credit-grid">
                                <div class="dialog-field-group">
                                    <label>Valor</label>
                                    <v-currency-field dense placeholder="0,00" v-model="manifestationForm.credit_value"
                                        :allow-negative="false" outlined hide-details="auto">
                                    </v-currency-field>
                                </div>
                                <div class="dialog-field-group">
                                    <label>Moeda</label>
                                    <v-select dense :items="dataset.currencyTypes" placeholder="Selecione"
                                        v-model="manifestationForm.credit_currency" item-value="id" item-text="name"
                                        outlined hide-details="auto" clearable>
                                    </v-select>
                                </div>
                                <div class="dialog-field-group manifestation-credit-class">
                                    <label>Classe</label>
                                    <v-select dense :items="dataset.creditorTypes" placeholder="Selecione a classe"
                                        v-model="manifestationForm.credit_class" item-value="id" item-text="name"
                                        outlined hide-details="auto" clearable>
                                    </v-select>
                                </div>
                            </div>
                        </section>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="dialogManifestation = false">Cancelar</v-btn>
                    <v-btn color="primary" elevation="0" @click="confirmAddManifestation"
                        :loading="dataForm.manifestationLoading">Adicionar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="dialogCreditResult" max-width="680px" content-class="proceedings-shadcn-dialog credit-result-dialog" scrollable persistent>
            <v-card>
                <v-card-title class="dialog-header">
                    <div><span>Adicionar crédito {{ creditResultForm.stage === 'appeal' ? 'do recurso' : 'da sentença' }}</span><small>Relacione o resultado processual ao crédito correspondente.</small></div>
                    <v-btn icon small aria-label="Fechar" @click="dialogCreditResult = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <v-form ref="creditResultForm" v-model="dataForm.creditResultFormValid" class="credit-result-form">
                        <section class="credit-result-section">
                            <div class="credit-result-section-heading">
                                <span class="credit-result-section-icon"><v-icon small>mdi-link-variant</v-icon></span>
                                <div><strong>Crédito relacionado</strong><small>Selecione a parte e o crédito afetado pelo resultado.</small></div>
                            </div>
                            <div class="dialog-fields-grid">
                                <div class="dialog-field-group">
                                    <label>Credor <em>*</em></label>
                                    <v-select dense :items="creditorParticipantItems" placeholder="Selecione o credor"
                                        v-model="creditResultForm.participant_id" item-value="id" item-text="name"
                                        outlined hide-details="auto" :rules="[$rules.required]" @change="creditResultForm.proceeding_credit_id = null">
                                    </v-select>
                                </div>
                                <div class="dialog-field-group">
                                    <label>Crédito <em>*</em></label>
                                    <v-select dense :items="creditItemsForSelectedParticipant" placeholder="Selecione o crédito"
                                        v-model="creditResultForm.proceeding_credit_id" item-value="id" item-text="name"
                                        outlined hide-details="auto" :rules="[$rules.required]" :disabled="!creditResultForm.participant_id">
                                    </v-select>
                                    <small v-if="!creditResultForm.participant_id" class="dialog-field-help">Selecione primeiro o credor.</small>
                                </div>
                            </div>
                        </section>

                        <section class="credit-result-section">
                            <div class="credit-result-section-heading">
                                <span class="credit-result-section-icon"><v-icon small>mdi-text-box-check-outline</v-icon></span>
                                <div><strong>Resultado processual</strong><small>Descreva o dispositivo aplicável a este crédito.</small></div>
                            </div>
                            <div class="dialog-field-group">
                                <label>Dispositivo ou resultado</label>
                                <v-textarea dense placeholder="Informe o resultado definido na decisão"
                                    v-model="creditResultForm.result_text" outlined hide-details="auto" rows="3">
                                </v-textarea>
                            </div>
                        </section>

                        <section class="credit-result-section credit-change-section" :class="{ active: creditResultForm.changed_credit }">
                            <button type="button" class="credit-change-toggle" :aria-expanded="creditResultForm.changed_credit" @click="creditResultForm.changed_credit = !creditResultForm.changed_credit">
                                <span class="credit-result-section-icon"><v-icon small>mdi-swap-horizontal</v-icon></span>
                                <span><strong>Alteração do crédito</strong><small>{{ creditResultForm.changed_credit ? 'Informe abaixo os dados finais definidos.' : 'Ative caso valor, classe ou moeda tenham sido alterados.' }}</small></span>
                                <span class="credit-change-state" :class="{ active: creditResultForm.changed_credit }">{{ creditResultForm.changed_credit ? 'Sim' : 'Não' }}</span>
                                <v-switch v-model="creditResultForm.changed_credit" hide-details class="ma-0" aria-label="Houve alteração no crédito?" @click.stop></v-switch>
                            </button>
                            <v-expand-transition>
                                <div v-show="creditResultForm.changed_credit" class="credit-final-fields">
                                    <div class="dialog-field-group">
                                        <label>Valor final</label>
                                        <v-currency-field dense placeholder="0,00" v-model="creditResultForm.final_value" :allow-negative="false" outlined hide-details="auto"></v-currency-field>
                                    </div>
                                    <div class="dialog-field-group">
                                        <label>Moeda</label>
                                        <v-select dense :items="dataset.currencyTypes" placeholder="Selecione" v-model="creditResultForm.final_currency" item-value="id" item-text="name" outlined hide-details="auto"></v-select>
                                    </div>
                                    <div class="dialog-field-group credit-final-class">
                                        <label>Classe final</label>
                                        <v-select dense :items="dataset.creditorTypes" placeholder="Selecione a classe" v-model="creditResultForm.final_class" item-value="id" item-text="name" outlined hide-details="auto"></v-select>
                                    </div>
                                </div>
                            </v-expand-transition>
                        </section>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="dialogCreditResult = false">Cancelar</v-btn>
                    <v-btn color="primary" elevation="0" @click="confirmAddCreditResult"
                        :loading="dataForm.creditResultLoading">Adicionar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="dialogDecision" max-width="820px" content-class="proceedings-shadcn-dialog decision-dialog" persistent scrollable>
            <v-card>
                <v-card-title class="dialog-header">
                    <div><span>Adicionar decisão intermediária</span><small>Registre os dados essenciais e anexe o documento da decisão.</small></div>
                    <v-btn icon small aria-label="Fechar" @click="dialogDecision = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text class="decision-dialog-body">
                    <v-form ref="decisionForm" v-model="dataForm.decisionFormValid" class="decision-form">
                        <section class="decision-form-section">
                            <header class="decision-section-header">
                                <span><v-icon size="18">mdi-text-box-outline</v-icon></span>
                                <div><strong>Identificação da decisão</strong><small>Informe como a decisão será localizada no acompanhamento processual.</small></div>
                            </header>
                            <div class="decision-fields-grid">
                                <div class="dialog-field-group decision-title-field">
                                    <label>Título <em>*</em></label>
                                    <v-text-field dense placeholder="Ex.: Despacho de intimação" v-model="decisionForm.title" outlined hide-details="auto" :rules="[$rules.required]" />
                                </div>
                                <div class="dialog-field-group">
                                    <label>Tipo</label>
                                    <v-select dense :items="dataset.decisionTypes" placeholder="Selecione o tipo" v-model="decisionForm.decision_type" item-value="id" item-text="name" outlined hide-details="auto" />
                                </div>
                                <div class="dialog-field-group">
                                    <label>Referência <span>Opcional</span></label>
                                    <v-text-field dense placeholder="Evento, ID ou movimento" v-model="decisionForm.reference" outlined hide-details="auto" />
                                </div>
                                <div class="dialog-field-group">
                                    <label>Data da decisão <span>Opcional</span></label>
                                    <v-text-field dense type="date" v-model="decisionForm.decided_at" outlined hide-details="auto" />
                                </div>
                                <div class="dialog-field-group decision-description-field">
                                    <label>Descrição <span>Opcional</span></label>
                                    <v-textarea dense placeholder="Resuma o conteúdo e as providências determinadas" v-model="decisionForm.description" outlined hide-details="auto" rows="3" />
                                </div>
                            </div>
                        </section>

                        <section class="decision-form-section decision-document-section">
                            <header class="decision-section-header">
                                <span><v-icon size="18">mdi-file-pdf-box</v-icon></span>
                                <div><strong>Documento da decisão</strong><small>Anexe o PDF utilizado como fonte deste registro.</small></div>
                            </header>

                            <input ref="decisionFileInput" class="decision-native-file" type="file" accept="application/pdf,.pdf" @change="onDecisionFileSelected" />
                            <div v-if="!decisionForm.file" class="decision-dropzone" :class="{ dragging: decisionFileDragActive }" role="button" tabindex="0"
                                @click="openDecisionFilePicker" @keydown.enter.prevent="openDecisionFilePicker" @keydown.space.prevent="openDecisionFilePicker"
                                @dragenter.prevent="decisionFileDragActive = true" @dragover.prevent="decisionFileDragActive = true" @dragleave.prevent="decisionFileDragActive = false" @drop.prevent="onDecisionFileDrop">
                                <span class="decision-dropzone-icon"><v-icon size="26">mdi-cloud-upload-outline</v-icon></span>
                                <div><strong>{{ decisionFileDragActive ? 'Solte o PDF aqui' : 'Arraste e solte o PDF da decisão' }}</strong><small>ou clique para selecionar um arquivo</small></div>
                                <span class="decision-file-rule">Somente PDF · até 20 MB</span>
                            </div>
                            <div v-else class="decision-selected-file">
                                <span class="decision-selected-icon"><v-icon size="24">mdi-file-pdf-box</v-icon></span>
                                <div><strong>{{ decisionForm.file.name }}</strong><small>{{ formatFileSize(decisionForm.file.size) }} · PDF pronto para envio</small></div>
                                <v-btn icon small aria-label="Remover PDF" title="Remover PDF" @click="removeDecisionFile"><v-icon size="18">mdi-close</v-icon></v-btn>
                            </div>
                        </section>

                        <button type="button" class="decision-visibility-card" :class="{ active: decisionForm.is_visible_to_creditor }" @click="decisionForm.is_visible_to_creditor = !decisionForm.is_visible_to_creditor">
                            <span class="decision-visibility-icon"><v-icon size="18">mdi-account-eye-outline</v-icon></span>
                            <span><strong>Disponibilizar ao credor</strong><small>Quando ativado, o documento poderá ser exibido no portal após o registro.</small></span>
                            <v-switch v-model="decisionForm.is_visible_to_creditor" hide-details class="ma-0" aria-label="Visível ao credor" @click.stop />
                        </button>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="dialogDecision = false">Cancelar</v-btn>
                    <v-btn color="primary" elevation="0" @click="confirmAddDecision"
                        :loading="dataForm.decisionLoading">Adicionar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="dialogReservation" max-width="760px" content-class="proceedings-shadcn-dialog reservation-dialog" scrollable persistent>
            <v-card>
                <v-card-title class="dialog-header">
                    <div><span>{{ reservationForm.id ? 'Editar reserva de crédito' : 'Criar reserva de crédito' }}</span><small>Defina o valor provisório e documente a justificativa da reserva.</small></div>
                    <v-btn icon small aria-label="Fechar" @click="dialogReservation = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <v-alert text color="primary" dense class="reservation-guidance">
                        A reserva é provisória e não altera automaticamente o crédito definitivo no QGC.
                    </v-alert>
                    <v-form ref="reservationForm" v-model="dataForm.reservationFormValid" class="reservation-form">
                        <section class="reservation-form-section">
                            <div class="reservation-section-heading"><strong>Vínculo do crédito</strong><span>Selecione a parte e, quando aplicável, o crédito que originou a reserva.</span></div>
                            <div class="reservation-grid reservation-grid-two">
                                <div class="dialog-field-group">
                                    <label>Credor <em>*</em></label>
                                    <v-select v-model="reservationForm.participant_id" :items="creditorParticipantItems" item-value="id" item-text="name" placeholder="Selecione o credor" outlined dense hide-details="auto" :rules="[$rules.required]" @change="onReservationParticipantChange" />
                                </div>
                                <div class="dialog-field-group">
                                    <label>Crédito relacionado <span>Opcional</span></label>
                                    <v-select v-model="reservationForm.proceeding_credit_id" :items="reservationCreditItems" item-value="id" item-text="name" placeholder="Selecione um crédito" outlined dense hide-details="auto" clearable @change="onReservationCreditChange" />
                                </div>
                            </div>
                        </section>

                        <section class="reservation-form-section">
                            <div class="reservation-section-heading"><strong>Valor reservado</strong><span>Informe o montante provisório e sua classificação.</span></div>
                            <div class="reservation-grid reservation-value-grid">
                                <div class="dialog-field-group">
                                    <label>Valor <em>*</em></label>
                                    <v-currency-field v-model="reservationForm.amount" placeholder="0,00" outlined dense hide-details="auto" :allow-negative="false" :rules="[$rules.required]" />
                                </div>
                                <div class="dialog-field-group">
                                    <label>Moeda <em>*</em></label>
                                    <v-select v-model="reservationForm.currency" :items="dataset.currencyTypes" item-value="id" item-text="name" placeholder="Selecione" outlined dense hide-details="auto" :rules="[$rules.required]" />
                                </div>
                                <div class="dialog-field-group">
                                    <label>Classe <em>*</em></label>
                                    <v-select v-model="reservationForm.class" :items="dataset.creditorTypes" item-value="id" item-text="name" placeholder="Selecione a classe" outlined dense hide-details="auto" :rules="[$rules.required]" />
                                </div>
                            </div>
                        </section>

                        <section class="reservation-form-section">
                            <div class="reservation-section-heading"><strong>Origem e fundamentação</strong><span>Documente a referência utilizada e o motivo da constituição da reserva.</span></div>
                            <div class="reservation-grid reservation-grid-two">
                                <div class="dialog-field-group">
                                    <label>Identificador da origem</label>
                                    <v-text-field v-model="reservationForm.origin_identifier" placeholder="Processo, pedido ou referência" outlined dense hide-details="auto" />
                                </div>
                                <div class="dialog-field-group">
                                    <label>Data de referência</label>
                                    <v-text-field v-model="reservationForm.reference_date" type="date" outlined dense hide-details="auto" />
                                </div>
                            </div>
                            <div class="dialog-field-group">
                                <label>Descrição da origem</label>
                                <v-textarea v-model="reservationForm.origin_description" placeholder="Descreva resumidamente a origem do valor" outlined dense hide-details="auto" rows="2" />
                            </div>
                            <div class="dialog-field-group">
                                <label>Justificativa <em>*</em></label>
                                <v-textarea v-model="reservationForm.justification" placeholder="Explique por que o valor deve ser reservado" outlined dense hide-details="auto" rows="3" :rules="[$rules.required]" />
                            </div>
                            <div class="dialog-field-group">
                                <label>Observações internas <span>Opcional</span></label>
                                <v-textarea v-model="reservationForm.notes" placeholder="Informações adicionais para a equipe" outlined dense hide-details="auto" rows="2" />
                            </div>
                        </section>
                    </v-form>
                </v-card-text>
                <v-card-actions><v-spacer /><v-btn text @click="dialogReservation = false">Cancelar</v-btn><v-btn color="primary" elevation="0" @click="saveReservation" :loading="dataForm.reservationLoading">{{ reservationForm.id ? 'Atualizar' : 'Criar reserva' }}</v-btn></v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="dialogCancelReservation" max-width="520" content-class="proceedings-shadcn-dialog" persistent>
            <v-card>
                <v-card-title class="dialog-header">
                    <div><span>Cancelar reserva de crédito</span><small>Informe o motivo para manter a alteração registrada no histórico.</small></div>
                    <v-btn icon small aria-label="Fechar" @click="dialogCancelReservation = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <v-alert type="warning" text>A reserva será preservada no histórico e não poderá ser editada após o cancelamento.</v-alert>
                    <div v-if="reservationToCancel" class="mb-4"><strong>{{ formatAmount(reservationToCancel.amount, reservationToCancel.currency) }}</strong> · {{ reservationToCancel.class | enum(dataset.creditorTypes, "id", "name") }}</div>
                    <v-textarea v-model="cancelReservationReason" label="Motivo do cancelamento" outlined dense rows="3" :rules="[$rules.required]" />
                </v-card-text>
                <v-card-actions><v-spacer /><v-btn text @click="dialogCancelReservation = false">Voltar</v-btn><v-btn color="error" elevation="0" @click="cancelReservation" :loading="dataForm.reservationLoading" :disabled="!cancelReservationReason">Confirmar cancelamento</v-btn></v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="dialogSaveConfirmation" max-width="480" content-class="proceedings-shadcn-dialog proceedings-alert-dialog" persistent>
            <v-card>
                <v-card-title class="alert-dialog-header">
                    {{ page.isNew ? 'Incluir impugnação?' : 'Atualizar impugnação?' }}
                </v-card-title>
                <v-card-text class="alert-dialog-description">
                    {{ page.isNew
                        ? 'Confirme para salvar e incluir esta impugnação.'
                        : 'Confirme para salvar as alterações realizadas nesta impugnação.' }}
                </v-card-text>
                <v-card-actions class="alert-dialog-actions">
                    <v-spacer />
                    <v-btn outlined :disabled="dataForm.saveLoading" @click="dialogSaveConfirmation = false">Cancelar</v-btn>
                    <v-btn color="primary" elevation="0" :loading="dataForm.saveLoading" @click="confirmSave">
                        {{ page.isNew ? 'Salvar' : 'Atualizar' }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        </template>

    </v-container>
</template>

<script>
import axios from "axios";

import proceedingTypes from "@/common/data/proceeding-types.data";
import appealResults from "@/common/data/proceeding-appeal-results.data";
import participantRoles from "@/common/data/proceeding-participant-roles.data";
import creditorTypes from "@/common/data/creditor-types.data";
import creditorOriginTypes from "@/common/data/creditor-origin-types.data";
import currencyTypes from "@/common/data/currency-types.data";
import pretensionTypes from "@/common/data/incident-pretension-types.data";
import statesList from "@/common/data/states-list.data";
import tribunalsList from "@/common/data/tribunals-list.data";
import ProceedingPetitions from "@/views/proceedings/components/ProceedingPetitions.vue";

export default {
    name: "ProceedingDetails",
    components: { ProceedingPetitions },

    data: () => ({
        user: null,
        initialLoading: true,
        page: {
            isNew: false,
            title: "",
        },

        newComment: "",
        activeSection: 0,
        petitionCount: 0,
        participantExpansion: {},
        processBlockExpansion: {
            manifestations: true,
            sentence: true,
            appeal: true,
            decisions: true,
        },

        // Dialog visibility flags
        dialogParticipant: false,
        dialogCredit: false,
        dialogManifestation: false,
        dialogCreditResult: false,
        dialogDecision: false,
        dialogParticipantCorrection: false,
        dialogReservation: false,
        decisionFileDragActive: false,
        dialogCancelReservation: false,
        dialogSaveConfirmation: false,
        reservationToCancel: null,
        cancelReservationReason: "",

        // Credit dialog context
        creditParticipant: null,
        creditParticipantIndex: null,
        creditEditing: null,
        creditEditingIndex: null,

        // Forms inside dialogs
        participantForm: {
            role: null,
            position: null,
            source: "qgc",
            user_creditor_id: null,
            name: "",
            registration: "",
        },
        participantCorrectionForm: {
            id: null,
            name: "",
            registration: "",
        },
        creditForm: {
            value: null,
            creditor_class: null,
            currency: null,
            pretension: null,
            origin_identifier: "",
            origin_description: "",
        },
        creditResultForm: {
            participant_id: null,
            proceeding_credit_id: null,
            stage: "sentence",
            result_text: "",
            changed_credit: false,
            final_value: null,
            final_class: null,
            final_currency: null,
        },
        decisionForm: {
            title: "",
            decision_type: "other",
            reference: "",
            description: "",
            decided_at: null,
            file: null,
            is_visible_to_creditor: false,
        },
        manifestationForm: {
            participant_id: null,
            ref: "",
            description: "",
            credit_value: null,
            credit_class: null,
            credit_currency: null,
        },
        reservationForm: {
            id: null,
            participant_id: null,
            proceeding_credit_id: null,
            origin_identifier: "",
            origin_description: "",
            amount: null,
            currency: 1,
            class: null,
            justification: "",
            reference_date: null,
            notes: "",
        },

        dataset: {
            breadcrumbItems: [],
            clients: [],
            admins: [],
            userCreditors: [],
            proceedingTypes: proceedingTypes,
            appealResults: appealResults,
            participantRoles: participantRoles,
            participantPositions: [
                { id: 'autor', name: 'Autor' },
                { id: 'reu', name: 'Réu' },
                { id: 'terceiro', name: 'Terceiro interessado' },
            ],
            decisionTypes: [
                { id: "suspension", name: "Suspensão" },
                { id: "diligence", name: "Diligência" },
                { id: "certificate_request", name: "Juntada/solicitação de certidão" },
                { id: "other", name: "Outra decisão" },
            ],
            creditorTypes: creditorTypes,
            creditorOriginTypes: creditorOriginTypes,
            currencyTypes: currencyTypes,
            pretensionTypes: pretensionTypes,
            statesList: statesList,
            tribunalsList: tribunalsList,
            requestKinds: [
                { id: "inclusion", name: "Inclusão de crédito", description: "Adicionar um crédito ainda não registrado.", icon: "mdi-plus-circle-outline" },
                { id: "change", name: "Alteração de crédito", description: "Modificar os dados de um crédito existente.", icon: "mdi-pencil-outline" },
            ],
            workflowStatuses: [
                { id: "pending", name: "Pendente" },
                { id: "in_analysis", name: "Em análise" },
                { id: "awaiting_information", name: "Aguardando informação" },
                { id: "completed", name: "Concluída" },
                { id: "closed", name: "Encerrada" },
            ],
            data: {
                mode: 1,
                client_id: null,
                process_number: "",
                type: null,
                distribution_date: null,
                request_kind: null,
                workflow_status: "pending",
                court_division: "",
                district: "",
                state: "",
                tribunal: "",
                internal_notes: "",
                responsible_admin_id: null,
                participants: [],
                reservations: [],
                audits: [],
                manifestations: [],
                credit_results: [],
                decisions: [],
                comments: [],
                has_sentence: false,
                sentence_result: "",
                sentence_changed_credit: false,
                sentence_final_value: null,
                sentence_final_class: null,
                sentence_final_currency: null,
                has_appeal: false,
                appeal_result: null,
                appeal_changed_credit: false,
                appeal_final_value: null,
                appeal_final_class: null,
                appeal_final_currency: null,
                final_judgment: false,
            },
        },

        dataForm: {
            validForm: true,
            saveLoading: false,
            participantLoading: false,
            creditLoading: false,
            manifestationLoading: false,
            creditResultLoading: false,
            decisionLoading: false,
            commentLoading: false,
            applyQgcLoading: false,
            userCreditorsLoading: false,
            participantFormValid: true,
            creditFormValid: true,
            manifestationFormValid: true,
            creditResultFormValid: true,
            decisionFormValid: true,
            reservationFormValid: true,
            reservationLoading: false,
        },
        reservationHeaders: [
            { text: "Credor", value: "participant" },
            { text: "Origem", value: "origin_identifier" },
            { text: "Valor", value: "amount" },
            { text: "Classe", value: "class" },
            { text: "Status", value: "status" },
            { text: "Criada em", value: "created_at" },
            { text: "Ações", value: "actions", sortable: false },
        ],
    }),

    computed: {
        summaryCreditors() {
            const participants = this.dataset.data.participants || [];
            const creditors = participants.filter(participant => Number(participant.role) === 1);
            return creditors.length ? creditors.map(participant => participant.name).join(", ") : "—";
        },

        hasCreditChange() {
            const results = this.dataset.data.credit_results || [];
            return results.some(result => result.changed_credit);
        },

        participantSelectItems() {
            if (!this.dataset.data.participants) return [];
            return this.dataset.data.participants.map(p => ({
                id: p.id || p._tempId,
                name: p.name || "Manifestante",
            }));
        },

        creditorParticipantItems() {
            if (!this.dataset.data.participants) return [];
            return this.dataset.data.participants
                .filter(participant => Number(participant.role) === 1)
                .map(participant => ({
                    id: participant.id || participant._tempId,
                    name: participant.name || "Credor",
                }));
        },

        creditItemsForSelectedParticipant() {
            const participant = (this.dataset.data.participants || []).find(item => {
                return (item.id || item._tempId) === this.creditResultForm.participant_id;
            });
            if (!participant || !participant.credits) return [];

            return participant.credits.map((credit, index) => ({
                id: credit.id || credit._tempId || index,
                name: `${credit.origin_identifier ? credit.origin_identifier + ' - ' : ''}${this.formatCurrency(credit.value)}`
            }));
        },

        reservationCreditItems() {
            const participant = (this.dataset.data.participants || []).find(item => Number(item.id || item._tempId) === Number(this.reservationForm.participant_id));
            if (!participant) return [];
            return (participant.credits || []).map(credit => ({
                id: credit.id,
                name: `${credit.origin_identifier ? credit.origin_identifier + " · " : ""}${this.formatAmount(credit.value, credit.currency)}`,
            }));
        },

        activeReservations() {
            return (this.dataset.data.reservations || []).filter(item => item.status === "active");
        },

        reservationSummary() {
            const totals = this.activeReservations.reduce((result, item) => {
                const currency = Number(item.currency || 1);
                result[currency] = Number(result[currency] || 0) + Number(item.amount || 0);
                return result;
            }, {});
            return Object.keys(totals).map(currency => ({
                currency: this.currencyCode(currency),
                formatted: this.formatAmount(totals[currency], currency),
            }));
        },

        missingFields() {
            const data = this.dataset.data;
            const fields = [];
            if (!data.distribution_date) fields.push("Data de distribuição");
            if (!data.request_kind) fields.push("Natureza do pedido");
            if (!data.court_division) fields.push("Vara");
            if (!data.district) fields.push("Comarca");
            if (!data.tribunal) fields.push("Tribunal");
            if (!(data.participants || []).some(item => Number(item.role) === 1)) fields.push("Credor");
            return fields;
        },

        responsibleName() {
            const relation = this.dataset.data.responsibleAdmin || this.dataset.data.responsible_admin;
            if (relation) return relation.name;
            const admin = this.dataset.admins.find(item => Number(item.id) === Number(this.dataset.data.responsible_admin_id));
            return admin ? admin.name : "Sem responsável";
        },

        canCreateReservation() {
            // Reservation-specific permissions are new and are not present in
            // every existing admin role yet. The proceeding is already behind
            // authenticated admin routes, so keep creation available here just
            // like the other editable sections of this screen.
            return !this.page.isNew;
        },

        canUpdateReservation() {
            return !this.page.isNew;
        },

        canCancelReservation() {
            return !this.page.isNew;
        },
    },

    created() {
        this.user = JSON.parse(localStorage.getItem("user"));
        this.loadData();
    },

    methods: {
        async loadData() {
            this.initialLoading = true;
            try {
                this.setPage();
                this.listBreadCrumbItems();
                await this.loadClients();
                await this.loadAdmins();
                await this.load();
            } finally {
                this.initialLoading = false;
            }
        },

        setPage() {
            this.page.isNew = this.$route.params.id === "novo";
            this.page.title = this.page.isNew ? "Nova impugnação" : "Detalhes da impugnação";
        },

        listBreadCrumbItems() {
            this.dataset.breadcrumbItems = [
                { text: "Homepage", disabled: false, to: "/home", exact: true },
                { text: "Impugnações", disabled: false, to: "/impugnacoes", exact: true },
                { text: this.page.isNew ? "Nova impugnação" : "Detalhes", disabled: true, exact: true },
            ];
        },

        formatDate(date) {
            if (!date) return "";
            return this.$moment(date).format("DD/MM/YYYY HH:mm");
        },

        formatShortDate(date) {
            return date ? this.$moment(date).format("DD/MM/YYYY") : "não informada";
        },

        requestKindLabel(value) {
            const item = this.dataset.requestKinds.find(entry => entry.id === value);
            return item ? item.name : "Natureza não informada";
        },

        workflowStatusLabel(value) {
            const item = this.dataset.workflowStatuses.find(entry => entry.id === value);
            return item ? item.name : "Pendente";
        },

        workflowStatusColor(value) {
            return { pending: "#f0f1f5", in_analysis: "#ececfa", awaiting_information: "#fff2d8", completed: "#e5f5eb", closed: "#edf0f4" }[value] || "#f0f1f5";
        },

        workflowStatusDotColor(value) {
            return { pending: "#8a90a3", in_analysis: "#3f4389", awaiting_information: "#c3832c", completed: "#358c5a", closed: "#6e7486" }[value] || "#8a90a3";
        },

        currencyCode(value) {
            const item = this.dataset.currencyTypes.find(entry => Number(entry.id) === Number(value));
            return item ? item.name : "BRL";
        },

        currencySymbol(value) {
            const item = this.dataset.currencyTypes.find(entry => Number(entry.id) === Number(value));
            if (item && item.currencyFormat) return item.currencyFormat;
            return Number(value) === 2 ? "US$" : Number(value) === 3 ? "€" : "R$";
        },

        formatAmount(value, currency) {
            return `${this.currencySymbol(currency)} ${Number(value || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },

        copyProcess() {
            if (navigator.clipboard) navigator.clipboard.writeText(this.dataset.data.process_number || "");
            this.$dialog.notify.info("Número do processo copiado", { position: "top-right", timeout: 1500 });
        },

        scrollTo(id) {
            this.$nextTick(() => {
                const element = document.getElementById(id);
                if (element) element.scrollIntoView({ behavior: "smooth", block: "start" });
            });
        },

        formatCurrency(value) {
            return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
        },

        formatClaimAmount(claim) {
            const currency = this.dataset.currencyTypes.find(item => Number(item.id) === Number(claim.currency));
            const symbol = currency ? currency.currencyFormat : "R$";
            return `${symbol} ${Number(claim.amount || 0).toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })}`;
        },

        formatRegistration(value) {
            const digits = String(value || "").replace(/\D/g, "");
            if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
            if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
            return value || "—";
        },

        participantInitials(name) {
            return String(name || "?")
                .split(" ")
                .filter(Boolean)
                .slice(0, 2)
                .map(part => part.charAt(0))
                .join("")
                .toUpperCase();
        },

        auditIcon(action) {
            const value = String(action || "").toLowerCase();
            if (value.includes("create")) return "mdi-plus-circle-outline";
            if (value.includes("delete") || value.includes("cancel")) return "mdi-close-circle-outline";
            if (value.includes("status")) return "mdi-swap-horizontal-circle-outline";
            if (value.includes("reservation")) return "mdi-lock-outline";
            if (value.includes("comment")) return "mdi-message-text-outline";
            return "mdi-pencil-outline";
        },

        participantExpansionKey(participant, index) {
            return String(participant.id || participant._tempId || `index-${index}`);
        },

        isParticipantExpanded(participant, index) {
            return this.participantExpansion[this.participantExpansionKey(participant, index)] !== false;
        },

        toggleParticipantExpansion(participant, index) {
            const key = this.participantExpansionKey(participant, index);
            this.$set(this.participantExpansion, key, !this.isParticipantExpanded(participant, index));
        },

        isProcessBlockExpanded(block) {
            return this.processBlockExpansion[block] !== false;
        },

        toggleProcessBlock(block) {
            this.$set(this.processBlockExpansion, block, !this.isProcessBlockExpanded(block));
        },

        getParticipantName(participantId) {
            if (!this.dataset.data.participants) return "—";
            const p = this.dataset.data.participants.find(x => (x.id || x._tempId) === participantId);
            return p ? p.name : "—";
        },

        stageCreditResults(stage) {
            return (this.dataset.data.credit_results || []).filter(result => result.stage === stage);
        },

        getCreditById(participantId, creditId) {
            const participant = (this.dataset.data.participants || []).find(item => {
                return (item.id || item._tempId) === participantId;
            });

            if (!participant || !participant.credits) return null;

            return participant.credits.find((credit, index) => {
                return (credit.id || credit._tempId || index) === creditId;
            }) || null;
        },

        getCreditLabel(result) {
            const credit = this.getCreditById(result.participant_id, result.proceeding_credit_id) || result.credit;
            if (!credit) return "Crédito vinculado";

            const origin = credit.origin_identifier ? `${credit.origin_identifier} - ` : "";
            const value = credit.value ? this.formatCurrency(credit.value) : "Valor não informado";

            return `${origin}${value}`;
        },

        getParticipantPositionLabel(position) {
            if (position === 1 || position === "1" || position === "autor") return "Autor";
            if (position === 2 || position === "2" || position === "reu") return "Réu";
            if (position === 3 || position === "3" || position === "terceiro") return "Terceiro interessado";
            return "—";
        },

        goBack() {
            this.$router.replace("/impugnacoes");
        },

        // ---- Load data ----

        async loadClients() {
            try {
                const result = await axios.get("/api/clients", { params: { start: 1, limit: 100000, search: {} } });
                const data = result.data.content.data;
                data.sort((a, b) => a.name.localeCompare(b.name));
                this.dataset.clients = data;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            }
        },

        async loadAdmins() {
            try {
                const result = await axios.get("/api/admin/list", { params: { start: 1, limit: 100000, search: {} } });
                this.dataset.admins = (result.data.content.data || []).sort((a, b) => a.name.localeCompare(b.name));
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            }
        },

        async load() {
            if (this.page.isNew) return;

            try {
                const result = await axios.get(`/api/admin/proceedings/${this.$route.params.id}`);
                const data = result.data.content || result.data;

                // NORMALIZA BOOLEANS
                data.has_sentence = !!data.has_sentence;
                data.sentence_changed_credit = !!data.sentence_changed_credit;
                data.has_appeal = !!data.has_appeal;
                data.appeal_changed_credit = !!data.appeal_changed_credit;
                data.final_judgment = !!data.final_judgment;
                data.qgc_updated = !!data.qgc_updated;
                data.mode = Number(data.mode || 1)
                data.distribution_date = data.distribution_date ? this.$moment(data.distribution_date).format("YYYY-MM-DD") : null;

                this.dataset.data = {
                    ...this.dataset.data,
                    ...data,
                    participants: data.participants || [],
                    manifestations: data.manifestations || [],
                    credit_results: data.creditResults || data.credit_results || [],
                    decisions: data.decisions || [],
                    comments: data.comments || [],
                    reservations: data.reservations || [],
                    audits: data.audits || [],

                };
                this.page.title = data.process_number || "Detalhes da impugnação";
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            }
        },

        async searchUserCreditors(query) {
            if (!query || query.length < 2) return;

            this.dataForm.userCreditorsLoading = true;
            try {
                const result = await axios.get("/api/admin/users-creditors-claim", { params: { search: { name: query, version: 3 }} });
                this.dataset.userCreditors = result.data.content.data || [];
            } catch (error) {
                // silent
            } finally {
                this.dataForm.userCreditorsLoading = false;
            }
        },

        // ---- Save (create/update) main proceeding ----

        async changeStatus(status) {
            try {
                const workflowStatus = status === 1 ? "completed" : "pending";
                await axios.patch(`/api/admin/proceedings/${this.$route.params.id}`, { status, workflow_status: workflowStatus });
                this.$set(this.dataset.data, 'status', status);
                this.$set(this.dataset.data, 'workflow_status', workflowStatus);
                this.$dialog.notify.info("Status atualizado!", { position: "top-right", timeout: 2000 });
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            }
        },

        save() {
            if (!this.$refs.form.validate()) return;

            this.dialogSaveConfirmation = true;
        },

        async confirmSave() {
            this.dataForm.saveLoading = true;

            const payload = this.buildPayload();

            try {
                if (this.page.isNew) {
                    const response = await axios.post("/api/admin/proceedings", payload);
                    const created = response.data.content || response.data;
                    this.dialogSaveConfirmation = false;
                    this.$dialog.notify.info("Impugnação criada com sucesso!", { position: "top-right", timeout: 2000 });
                    this.$router.replace(`/impugnacoes/${created.id}`);
                } else {
                    await axios.patch(`/api/admin/proceedings/${this.$route.params.id}`, payload);
                    this.dialogSaveConfirmation = false;
                    this.$dialog.notify.info("Impugnação atualizada com sucesso!", { position: "top-right", timeout: 2000 });
                    this.load();
                }
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            } finally {
                this.dataForm.saveLoading = false;
            }
        },

        buildPayload() {
            const d = this.dataset.data;
            return {
                client_id: d.client_id,
                process_number: d.process_number,
                type: d.type,
                mode: d.mode,
                has_sentence: d.has_sentence,
                sentence_result: d.sentence_result,
                sentence_changed_credit: d.sentence_changed_credit,
                sentence_final_value: d.sentence_final_value,
                sentence_final_class: d.sentence_final_class,
                sentence_final_currency: d.sentence_final_currency,
                has_appeal: d.has_appeal,
                appeal_result: d.appeal_result,
                appeal_changed_credit: d.appeal_changed_credit,
                appeal_final_value: d.appeal_final_value,
                appeal_final_class: d.appeal_final_class,
                appeal_final_currency: d.appeal_final_currency,
                final_judgment: d.final_judgment,
                distribution_date: d.distribution_date,
                request_kind: d.request_kind,
                workflow_status: d.workflow_status,
                court_division: d.court_division,
                district: d.district,
                state: d.state ? String(d.state).toUpperCase() : null,
                tribunal: d.tribunal,
                internal_notes: d.internal_notes,
                responsible_admin_id: d.responsible_admin_id,
                participants: d.participants || [],
                manifestations: d.manifestations || [],
                credit_results: d.credit_results || [],
                decisions: d.decisions || [],
            };
        },

        // ---- Participants ----

        openParticipantDialog() {
            this.participantForm = {
                role: null,
                position: null,
                source: "qgc",
                user_creditor_id: null,
                name: "",
                registration: "",
            };
            this.dialogParticipant = true;
        },

        async confirmAddParticipant() {

            if (!this.$refs.participantForm.validate()) return;

            if (this.page.isNew) {

                // ADD LOCAL

                const selectedCreditor =
                    this.dataset.userCreditors.find(
                        x => x.id === this.participantForm.user_creditor_id
                    ) || {};

                const tempParticipant = {
                    _tempId: Date.now(),
                    role: this.participantForm.role,
                    position: this.participantForm.position,
                    source: this.participantForm.source,
                    user_creditor_id: this.participantForm.user_creditor_id,

                    name:
                        this.participantForm.source === "qgc"
                            ? selectedCreditor.name || "Credor QGC"
                            : this.participantForm.name,

                    registration:
                        this.participantForm.source === "qgc"
                            ? selectedCreditor.registration || null
                            : this.participantForm.registration,

                    credits: [],
                };

                this.dataset.data.participants.push(tempParticipant);

                this.dialogParticipant = false;

                return;
            }

            this.dataForm.participantLoading = true;

            try {

                const selectedCreditor =
                    this.dataset.userCreditors.find(
                        x => x.id === this.participantForm.user_creditor_id
                    ) || {};

                const payload = {
                    role: this.participantForm.role,
                    position: this.participantForm.position,
                    source: this.participantForm.source,
                    user_creditor_id: this.participantForm.user_creditor_id,

                    name:
                        this.participantForm.source === "qgc"
                            ? selectedCreditor.name || null
                            : this.participantForm.name,

                    registration:
                        this.participantForm.source === "qgc"
                            ? selectedCreditor.registration || null
                            : this.participantForm.registration,
                };

                await axios.post(
                    `/api/admin/proceedings/${this.$route.params.id}/participants`,
                    payload
                );

                this.$dialog.notify.info(
                    "Manifestante adicionado com sucesso!",
                    {
                        position: "top-right",
                        timeout: 2000
                    }
                );

                this.dialogParticipant = false;

                await this.load();

            } catch (error) {

                this.$dialog.notify.error(
                    this.$codes(error),
                    {
                        position: "top-right",
                        timeout: 2000
                    }
                );

            } finally {
                this.dataForm.participantLoading = false;
            }
        },

        async removeParticipant(participant, index) {
            const proceedAction = await this.$dialog.confirm({
                text: "Tem certeza que deseja excluir este manifestante?",
                title: "Excluir manifestante",
                actions: { false: "Cancelar", true: "Excluir" },
            });

            if (!proceedAction) return;

            if (this.page.isNew || !participant.id) {
                this.dataset.data.participants.splice(index, 1);
                return;
            }

            try {
                await axios.delete(`/api/admin/proceedings/participants/${participant.id}`);
                this.$dialog.notify.info("Manifestante removido com sucesso!", { position: "top-right", timeout: 2000 });
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            }
        },

        openParticipantCorrection(participant) {
            this.participantCorrectionForm = {
                id: participant.id,
                name: participant.name || "",
                registration: participant.registration || participant.suggested_registration || "",
            };
            this.dialogParticipantCorrection = true;
        },

        async saveParticipantCorrection() {
            if (!this.participantCorrectionForm.id) return;

            this.dataForm.participantLoading = true;
            try {
                await axios.patch(`/api/admin/proceedings/participants/${this.participantCorrectionForm.id}`, {
                    name: this.participantCorrectionForm.name,
                    registration: this.participantCorrectionForm.registration,
                });
                this.$dialog.notify.info("Manifestante atualizado com sucesso!", { position: "top-right", timeout: 2000 });
                this.dialogParticipantCorrection = false;
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            } finally {
                this.dataForm.participantLoading = false;
            }
        },

        async acceptParticipantSuggestion(participant) {
            if (!participant.id) return;

            this.dataForm.participantLoading = true;
            try {
                await axios.patch(`/api/admin/proceedings/participants/${participant.id}`, {
                    accept_suggestion: true,
                });
                this.$dialog.notify.info("CPF/CNPJ sugerido confirmado!", { position: "top-right", timeout: 2000 });
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            } finally {
                this.dataForm.participantLoading = false;
            }
        },

        // ---- Credits ----

        openCreditDialog(participant, pIndex, credit = null, cIndex = null) {
            this.creditParticipant = participant;
            this.creditParticipantIndex = pIndex;
            this.creditEditing = credit;
            this.creditEditingIndex = cIndex;
            this.creditForm = credit ? {
                value: credit.value,
                creditor_class: credit.class,
                currency: credit.currency,
                pretension: credit.pretension,
                origin_identifier: credit.origin_identifier || "",
                origin_description: credit.origin_description || "",
            } : {
                value: null,
                creditor_class: null,
                currency: null,
                pretension: null,
                origin_identifier: "",
                origin_description: "",
            };
            this.dialogCredit = true;
        },

        async confirmCredit() {
            if (!this.$refs.creditForm.validate()) return;

            const payload = {
                value: this.creditForm.value,
                class: this.creditForm.creditor_class,
                currency: this.creditForm.currency,
                pretension: this.creditForm.pretension,
                origin_identifier: this.creditForm.origin_identifier,
                origin_description: this.creditForm.origin_description,
            };

            if (this.page.isNew || !this.creditParticipant.id) {
                if (!this.creditParticipant.credits) {
                    this.$set(this.creditParticipant, "credits", []);
                }
                if (this.creditEditing) {
                    this.$set(this.creditParticipant.credits, this.creditEditingIndex, {
                        ...this.creditEditing,
                        ...payload,
                    });
                } else {
                    this.creditParticipant.credits.push({ _tempId: Date.now(), ...payload });
                }
                this.dialogCredit = false;
                return;
            }

            this.dataForm.creditLoading = true;

            try {
                if (this.creditEditing && this.creditEditing.id) {
                    await axios.patch(`/api/admin/proceedings/credits/${this.creditEditing.id}`, payload);
                } else {
                    await axios.post(
                        `/api/admin/proceedings/participants/${this.creditParticipant.id}/credits`,
                        payload
                    );
                }
                this.$dialog.notify.info(this.creditEditing ? "Crédito atualizado com sucesso!" : "Crédito adicionado com sucesso!", { position: "top-right", timeout: 2000 });
                this.dialogCredit = false;
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            } finally {
                this.dataForm.creditLoading = false;
            }
        },

        async removeCredit(participant, pIndex, credit, cIndex) {
            const proceedAction = await this.$dialog.confirm({
                text: "Tem certeza que deseja excluir este crédito?",
                title: "Excluir crédito",
                actions: { false: "Cancelar", true: "Excluir" },
            });

            if (!proceedAction) return;

            if (this.page.isNew || !credit.id) {
                participant.credits.splice(cIndex, 1);
                return;
            }

            try {
                await axios.delete(`/api/admin/proceedings/credits/${credit.id}`);
                this.$dialog.notify.info("Crédito removido com sucesso!", { position: "top-right", timeout: 2000 });
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            }
        },

        // ---- Credit reservations ----

        openReservationDialog(reservation) {
            if (!reservation && !this.creditorParticipantItems.length) {
                this.activeSection = 1;
                this.$dialog.notify.error("Adicione um credor antes de criar a reserva.", { position: "top-right", timeout: 3000 });
                return;
            }

            const defaultParticipant = !reservation && this.creditorParticipantItems.length === 1
                ? this.creditorParticipantItems[0].id
                : null;
            this.reservationForm = reservation ? {
                id: reservation.id,
                participant_id: reservation.participant_id,
                proceeding_credit_id: reservation.proceeding_credit_id,
                origin_identifier: reservation.origin_identifier || "",
                origin_description: reservation.origin_description || "",
                amount: Number(reservation.amount),
                currency: Number(reservation.currency || 1),
                class: Number(reservation.class),
                justification: reservation.justification || "",
                reference_date: reservation.reference_date ? this.$moment(reservation.reference_date).format("YYYY-MM-DD") : null,
                notes: reservation.notes || "",
            } : {
                id: null, participant_id: defaultParticipant, proceeding_credit_id: null, origin_identifier: "", origin_description: "",
                amount: null, currency: 1, class: null, justification: "", reference_date: null, notes: "",
            };
            if (!reservation && defaultParticipant) this.prefillReservationFromParticipant();
            this.dialogReservation = true;
        },

        onReservationParticipantChange() {
            this.reservationForm.proceeding_credit_id = null;
            this.prefillReservationFromParticipant();
        },

        prefillReservationFromParticipant() {
            const participant = (this.dataset.data.participants || []).find(item =>
                Number(item.id || item._tempId) === Number(this.reservationForm.participant_id)
            );
            const credits = participant && participant.credits ? participant.credits : [];
            if (credits.length === 1) {
                const credit = credits[0];
                this.reservationForm.proceeding_credit_id = credit.id || credit._tempId || null;
                this.applyCreditToReservation(credit);
            }
        },

        onReservationCreditChange(creditId) {
            if (!creditId) return;
            const participant = (this.dataset.data.participants || []).find(item =>
                Number(item.id || item._tempId) === Number(this.reservationForm.participant_id)
            );
            const credit = participant && (participant.credits || []).find(item =>
                Number(item.id || item._tempId) === Number(creditId)
            );
            if (credit) this.applyCreditToReservation(credit);
        },

        applyCreditToReservation(credit) {
            this.reservationForm.amount = credit.value || credit.amount || null;
            this.reservationForm.currency = Number(credit.currency || 1);
            this.reservationForm.class = Number(credit.class || credit.creditor_class) || null;
            this.reservationForm.origin_identifier = credit.origin_identifier || "";
            this.reservationForm.origin_description = credit.origin_description || "";
        },

        async saveReservation() {
            if (!this.$refs.reservationForm.validate()) return;
            this.dataForm.reservationLoading = true;
            const id = this.reservationForm.id;
            const payload = { ...this.reservationForm };
            delete payload.id;
            try {
                if (id) await axios.patch(`/api/admin/proceedings/${this.$route.params.id}/reservations/${id}`, payload);
                else await axios.post(`/api/admin/proceedings/${this.$route.params.id}/reservations`, payload);
                this.$dialog.notify.info(id ? "Reserva atualizada com sucesso" : "Reserva criada com sucesso", { position: "top-right", timeout: 2000 });
                this.dialogReservation = false;
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2500 });
            } finally {
                this.dataForm.reservationLoading = false;
            }
        },

        openCancelReservation(reservation) {
            this.reservationToCancel = reservation;
            this.cancelReservationReason = "";
            this.dialogCancelReservation = true;
        },

        async cancelReservation() {
            if (!this.reservationToCancel || !this.cancelReservationReason) return;
            this.dataForm.reservationLoading = true;
            try {
                await axios.patch(`/api/admin/proceedings/${this.$route.params.id}/reservations/${this.reservationToCancel.id}/cancel`, { reason: this.cancelReservationReason });
                this.$dialog.notify.info("Reserva cancelada e preservada no histórico", { position: "top-right", timeout: 2200 });
                this.dialogCancelReservation = false;
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2500 });
            } finally {
                this.dataForm.reservationLoading = false;
            }
        },

        // ---- Credit results ----

        openCreditResultDialog(stage) {
            this.creditResultForm = {
                participant_id: null,
                proceeding_credit_id: null,
                stage,
                result_text: "",
                changed_credit: true,
                final_value: null,
                final_class: null,
                final_currency: null,
            };
            this.dialogCreditResult = true;
        },

        async confirmAddCreditResult() {
            if (!this.$refs.creditResultForm.validate()) return;

            if (this.page.isNew) {
                this.dataset.data.credit_results.push({ ...this.creditResultForm, _tempId: Date.now() });
                this.dialogCreditResult = false;
                return;
            }

            this.dataForm.creditResultLoading = true;

            try {
                await axios.post(`/api/admin/proceedings/${this.$route.params.id}/credit-results`, this.creditResultForm);
                this.$dialog.notify.info("Resultado adicionado com sucesso!", { position: "top-right", timeout: 2000 });
                this.dialogCreditResult = false;
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            } finally {
                this.dataForm.creditResultLoading = false;
            }
        },

        async removeCreditResult(result) {
            const proceedAction = await this.$dialog.confirm({
                text: "Tem certeza que deseja excluir este crédito vinculado?",
                title: "Excluir crédito vinculado",
                actions: { false: "Cancelar", true: "Excluir" },
            });

            if (!proceedAction) return;

            if (this.page.isNew || !result.id) {
                const index = this.dataset.data.credit_results.findIndex(item => item === result || item._tempId === result._tempId);
                if (index >= 0) this.dataset.data.credit_results.splice(index, 1);
                return;
            }

            try {
                await axios.delete(`/api/admin/proceedings/credit-results/${result.id}`);
                this.$dialog.notify.info("Resultado removido com sucesso!", { position: "top-right", timeout: 2000 });
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            }
        },

        // ---- Manifestations ----

        openManifestationDialog() {
            this.manifestationForm = {
                participant_id: null,
                ref: "",
                description: "",
                credit_value: null,
                credit_class: null,
                credit_currency: null,
            };
            this.dialogManifestation = true;
        },

        // ---- Decisions ----

        openDecisionDialog() {
            this.decisionForm = {
                title: "",
                decision_type: "other",
                reference: "",
                description: "",
                decided_at: null,
                file: null,
                is_visible_to_creditor: false,
            };
            this.decisionFileDragActive = false;
            this.$nextTick(() => {
                if (this.$refs.decisionFileInput) this.$refs.decisionFileInput.value = "";
            });
            this.dialogDecision = true;
        },

        openDecisionFilePicker() {
            if (this.$refs.decisionFileInput) this.$refs.decisionFileInput.click();
        },

        onDecisionFileSelected(event) {
            const file = event && event.target && event.target.files ? event.target.files[0] : null;
            this.setDecisionFile(file);
        },

        onDecisionFileDrop(event) {
            this.decisionFileDragActive = false;
            const file = event && event.dataTransfer && event.dataTransfer.files ? event.dataTransfer.files[0] : null;
            this.setDecisionFile(file);
        },

        setDecisionFile(file) {
            if (!file) return;
            const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
            if (!isPdf) {
                this.$dialog.notify.error("Selecione um arquivo no formato PDF.", { position: "top-right", timeout: 2500 });
                return;
            }
            if (Number(file.size || 0) > 20 * 1024 * 1024) {
                this.$dialog.notify.error("O PDF deve possuir no máximo 20 MB.", { position: "top-right", timeout: 2500 });
                return;
            }
            this.$set(this.decisionForm, "file", file);
        },

        removeDecisionFile() {
            this.$set(this.decisionForm, "file", null);
            if (this.$refs.decisionFileInput) this.$refs.decisionFileInput.value = "";
        },

        formatFileSize(size) {
            const bytes = Number(size || 0);
            if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
            return `${(bytes / (1024 * 1024)).toFixed(1).replace(".0", "")} MB`;
        },

        async confirmAddDecision() {
            if (!this.$refs.decisionForm.validate()) return;

            if (this.page.isNew && this.decisionForm.file) {
                this.$dialog.notify.info("Salve a impugnação antes de anexar o arquivo da decisão.", { position: "top-right", timeout: 2500 });
                return;
            }

            if (this.page.isNew) {
                this.dataset.data.decisions.push({ ...this.decisionForm });
                this.dialogDecision = false;
                return;
            }

            this.dataForm.decisionLoading = true;

            const payload = new FormData();
            Object.keys(this.decisionForm).forEach(key => {
                if (this.decisionForm[key] !== null && this.decisionForm[key] !== undefined && this.decisionForm[key] !== "") {
                    payload.append(key, this.decisionForm[key]);
                }
            });

            try {
                await axios.post(`/api/admin/proceedings/${this.$route.params.id}/decisions`, payload);
                this.$dialog.notify.info("Decisão adicionada com sucesso!", { position: "top-right", timeout: 2000 });
                this.dialogDecision = false;
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            } finally {
                this.dataForm.decisionLoading = false;
            }
        },

        async removeDecision(decision, index) {
            const proceedAction = await this.$dialog.confirm({
                text: "Tem certeza que deseja excluir esta decisão?",
                title: "Excluir decisão",
                actions: { false: "Cancelar", true: "Excluir" },
            });

            if (!proceedAction) return;

            if (this.page.isNew || !decision.id) {
                this.dataset.data.decisions.splice(index, 1);
                return;
            }

            try {
                await axios.delete(`/api/admin/proceedings/decisions/${decision.id}`);
                this.$dialog.notify.info("Decisão removida com sucesso!", { position: "top-right", timeout: 2000 });
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            }
        },

        async confirmAddManifestation() {
            if (!this.$refs.manifestationForm.validate()) return;

            if (this.page.isNew) {
                this.dataset.data.manifestations.push({ ...this.manifestationForm });
                this.dialogManifestation = false;
                return;
            }

            this.dataForm.manifestationLoading = true;

            try {
                await axios.post(
                    `/api/admin/proceedings/${this.$route.params.id}/manifestations`,
                    this.manifestationForm
                );
                this.$dialog.notify.info("Manifestação adicionada com sucesso!", { position: "top-right", timeout: 2000 });
                this.dialogManifestation = false;
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            } finally {
                this.dataForm.manifestationLoading = false;
            }
        },

        async removeManifestation(manifestation, index) {
            const proceedAction = await this.$dialog.confirm({
                text: "Tem certeza que deseja excluir esta manifestação?",
                title: "Excluir manifestação",
                actions: { false: "Cancelar", true: "Excluir" },
            });

            if (!proceedAction) return;

            if (this.page.isNew || !manifestation.id) {
                this.dataset.data.manifestations.splice(index, 1);
                return;
            }

            try {
                await axios.delete(`/api/admin/proceedings/manifestations/${manifestation.id}`);
                this.$dialog.notify.info("Manifestação removida com sucesso!", { position: "top-right", timeout: 2000 });
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            }
        },

        // ---- Comments ----

        async addComment() {
            if (!this.newComment) return;

            this.dataForm.commentLoading = true;

            try {
                await axios.post(`/api/admin/proceedings/${this.$route.params.id}/comments`, {
                    content: this.newComment,
                });
                this.$dialog.notify.info("Comentário adicionado!", { position: "top-right", timeout: 2000 });
                this.newComment = "";
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            } finally {
                this.dataForm.commentLoading = false;
            }
        },

        // ---- QGC ----

        async applyQgc() {
            const proceedAction = await this.$dialog.confirm({
                text: "Deseja autorizar a alteração no QGC com base na sentença?",
                title: "Autorizar alteração QGC",
                actions: { false: "Cancelar", true: "Autorizar" },
            });

            if (!proceedAction) return;

            this.dataForm.applyQgcLoading = true;

            try {
                await axios.post(`/api/admin/proceedings/${this.$route.params.id}/apply-qgc`);
                this.$dialog.notify.info("Alteração QGC autorizada com sucesso!", { position: "top-right", timeout: 2000 });
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            } finally {
                this.dataForm.applyQgcLoading = false;
            }
        },
    },
};
</script>

<style lang="scss">
.proceeding-detail-skeleton {
    --skeleton-base: #edf0f4;
    --skeleton-highlight: #f8f9fb;
    min-height: calc(100vh - 96px);
}

.skeleton-block,
.skeleton-line {
    background: linear-gradient(90deg, var(--skeleton-base) 25%, var(--skeleton-highlight) 50%, var(--skeleton-base) 75%);
    background-size: 200% 100%;
    border-radius: 7px;
    animation: proceeding-skeleton-shimmer 1.45s ease-in-out infinite;
}

.detail-skeleton-toolbar,
.detail-skeleton-actions,
.detail-skeleton-facts,
.detail-skeleton-section-heading,
.detail-skeleton-fields {
    align-items: center;
    display: flex;
}

.detail-skeleton-toolbar {
    justify-content: space-between;
    margin-bottom: 12px;
    min-height: 38px;
}

.detail-skeleton-actions { gap: 8px; }
.skeleton-breadcrumb { height: 11px; width: 210px; }
.skeleton-action { height: 36px; width: 72px; }
.skeleton-action-wide { width: 118px; }

.detail-skeleton-card {
    background: #fff;
    border: 1px solid #e1e4ec;
    border-radius: 12px;
}

.detail-skeleton-hero {
    margin-bottom: 16px;
    padding: 20px;
}

.skeleton-eyebrow { height: 9px; width: 285px; }
.skeleton-title { height: 24px; margin-top: 10px; width: 150px; }
.skeleton-status { height: 10px; margin-top: 12px; width: 78px; }
.detail-skeleton-facts { gap: 10px; margin-top: 18px; }
.skeleton-fact { height: 66px; max-width: 190px; padding: 12px; width: 100%; }
.skeleton-fact-label { height: 8px; width: 70%; }
.skeleton-fact-value { height: 12px; margin-top: 11px; width: 55%; }

.detail-skeleton-tabs {
    display: flex;
    gap: 34px;
    margin-bottom: 16px;
    padding: 17px 18px;
}

.skeleton-tab { height: 10px; width: 110px; }
.skeleton-tab:nth-child(2) { width: 132px; }
.skeleton-tab:nth-child(4) { width: 145px; }

.detail-skeleton-content { padding: 20px; }
.detail-skeleton-section-heading { justify-content: space-between; }
.skeleton-section-title { height: 17px; width: 155px; }
.skeleton-pending { height: 10px; width: 115px; }
.skeleton-toggle { height: 32px; margin: 20px 0 28px; width: 158px; }
.detail-skeleton-fields { align-items: stretch; gap: 16px; margin-bottom: 18px; }
.detail-skeleton-fields-main .skeleton-field { flex-basis: 33.333%; }
.skeleton-field { flex: 1; height: 52px; }
.skeleton-textarea { height: 92px; width: 100%; }

.sr-only {
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    width: 1px;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
}

@keyframes proceeding-skeleton-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
}

@media (prefers-reduced-motion: reduce) {
    .proceeding-detail-skeleton .skeleton-block,
    .proceeding-detail-skeleton .skeleton-line { animation: none; }
}

@media (max-width: 960px) {
    .detail-skeleton-actions .skeleton-action:first-child { display: none; }
    .detail-skeleton-facts,
    .detail-skeleton-fields { flex-wrap: wrap; }
    .skeleton-fact,
    .skeleton-field { flex: 1 1 calc(50% - 8px); }
    .detail-skeleton-tabs { gap: 20px; overflow: hidden; }
}

.btn-toggle-gap .v-btn {
    margin-right: 8px !important;
}

.proceeding-hero,
.section-navigation {
    border: 1px solid #e1e4ec !important;
    border-radius: 12px !important;
}

.process-heading {
    color: #25275d;
    font-family: monospace;
    font-size: 24px;
    line-height: 1.3;
}

.process-eyebrow {
    color: #777d8f;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: .04em;
    margin-bottom: 3px;
    text-transform: uppercase;
}

.hero-status {
    align-items: center;
    color: #4f5468;
    display: inline-flex;
    font-size: 12px;
    font-weight: 600;
    gap: 7px;
}

.hero-status > span {
    border-radius: 50%;
    height: 8px;
    width: 8px;
}

.hero-facts {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.hero-fact {
    background: #fafbfc;
    border: 1px solid #e7e9ef;
    border-radius: 9px;
    display: flex;
    flex-direction: column;
    min-width: 170px;
    padding: 10px 13px;
}

.hero-fact span {
    color: #777d8f;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
}

.hero-fact strong {
    color: #30334a;
    font-size: 13px;
    margin-top: 3px;
}

.hero-fact.missing {
    background: #fffaf0;
    border-color: #ebd39f;
}

.hero-fact button {
    color: #343778;
    font-size: 11px;
    font-weight: 700;
    margin-top: 4px;
    text-align: left;
}

.tab-count {
    background: #eef0f5;
    border-radius: 10px;
    color: #60667a;
    font-size: 10px;
    line-height: 18px;
    margin-left: 6px;
    min-width: 18px;
    padding: 0 5px;
    text-align: center;
}

.clean-detail-card {
    border: 1px solid #e1e4ec !important;
    border-radius: 12px !important;
    box-shadow: none !important;
    overflow: hidden;
}

.missing-note {
    align-items: center;
    color: #946b1e;
    display: inline-flex;
    font-size: 11px;
    font-weight: 500;
    gap: 4px;
}

.distribution-field-missing .v-input__slot {
    background: #fffdf7 !important;
}

.field-hint {
    color: #936d28;
    display: block;
    font-size: 10px;
    margin: 4px 2px 0;
}

.empty-section {
    align-items: center;
    color: #73788a;
    display: flex;
    flex-direction: column;
    gap: 7px;
    justify-content: center;
    min-height: 180px;
    padding: 28px;
    text-align: center;
}

.empty-section strong {
    color: #3d4155;
    font-size: 14px;
}

.empty-section span {
    font-size: 12px;
}

.section-navigation {
    position: sticky;
    top: 0;
    z-index: 3;
}

.section-anchor {
    scroll-margin-top: 75px;
}

.reservation-help {
    color: #73788a;
    font-size: 12px;
    font-weight: 400;
    margin-top: 3px;
    max-width: 720px;
}

.reservation-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}

.reservation-summary > div {
    border: 1px solid #e3e4ed;
    border-radius: 9px;
    min-width: 190px;
    padding: 11px 14px;
    display: flex;
    flex-direction: column;
}

.reservation-summary span {
    color: #74798b;
    font-size: 11px;
    text-transform: uppercase;
}

.reservation-summary strong {
    color: #25275d;
    font-size: 18px;
    margin-top: 3px;
}

.reservation-row-actions {
    align-items: center;
    display: flex;
    gap: 3px;
    justify-content: flex-end;
    min-height: 28px;
}

.reservation-row-actions .v-btn {
    height: 28px;
    margin: 0;
    width: 28px;
}

.reservation-row-actions .v-icon {
    margin: 0;
}

.decision-dialog-body {
    background: #f8f9fb;
    padding: 18px !important;
}

.decision-form {
    display: grid;
    gap: 14px;
}

.decision-form-section {
    background: #fff;
    border: 1px solid #e1e4ec;
    border-radius: 10px;
    overflow: hidden;
}

.decision-section-header {
    align-items: center;
    border-bottom: 1px solid #e9ebf0;
    display: flex;
    gap: 10px;
    padding: 12px 14px;
}

.decision-section-header > span,
.decision-visibility-icon {
    align-items: center;
    background: #eef2f6;
    border-radius: 7px;
    color: #31536f;
    display: flex;
    flex: 0 0 auto;
    height: 34px;
    justify-content: center;
    width: 34px;
}

.decision-section-header > div,
.decision-visibility-card > span:nth-child(2) {
    display: flex;
    flex-direction: column;
}

.decision-section-header strong,
.decision-visibility-card strong {
    color: #303446;
    font-size: 12px;
}

.decision-section-header small,
.decision-visibility-card small {
    color: #7b8192;
    font-size: 10px;
    margin-top: 2px;
}

.decision-fields-grid {
    display: grid;
    gap: 14px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 15px;
}

.decision-description-field {
    grid-column: 1 / -1;
}

.decision-fields-grid .dialog-field-group > label {
    color: #343747;
    display: block;
    font-size: 10px;
    font-weight: 650;
    margin: 0 0 6px 1px;
}

.decision-fields-grid .dialog-field-group label em {
    color: #dc2626;
    font-style: normal;
}

.decision-fields-grid .dialog-field-group label span {
    color: #959aaa;
    font-size: 8px;
    font-weight: 500;
    margin-left: 4px;
    text-transform: uppercase;
}

.decision-document-section {
    padding-bottom: 15px;
}

.decision-native-file {
    display: none;
}

.decision-dropzone {
    align-items: center;
    background: #fbfcfd;
    border: 1px dashed #b8c2cf;
    border-radius: 9px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 6px;
    justify-content: center;
    margin: 15px;
    min-height: 142px;
    outline: none;
    padding: 20px;
    text-align: center;
    transition: background-color .15s ease, border-color .15s ease, box-shadow .15s ease;
}

.decision-dropzone:hover,
.decision-dropzone:focus,
.decision-dropzone.dragging {
    background: #f3f8fc;
    border-color: #315f81;
    box-shadow: 0 0 0 3px rgba(49, 95, 129, .1);
}

.decision-dropzone-icon {
    align-items: center;
    background: #eaf0f5;
    border-radius: 50%;
    color: #315f81;
    display: flex;
    height: 48px;
    justify-content: center;
    width: 48px;
}

.decision-dropzone > div {
    display: flex;
    flex-direction: column;
}

.decision-dropzone strong {
    color: #303446;
    font-size: 12px;
}

.decision-dropzone small {
    color: #777d8e;
    font-size: 10px;
    margin-top: 2px;
}

.decision-file-rule {
    color: #989dac;
    font-size: 8px;
    text-transform: uppercase;
}

.decision-selected-file {
    align-items: center;
    background: #f7faf8;
    border: 1px solid #cfe2d6;
    border-radius: 9px;
    display: grid;
    gap: 11px;
    grid-template-columns: 42px minmax(0, 1fr) 32px;
    margin: 15px;
    padding: 12px;
}

.decision-selected-icon {
    align-items: center;
    background: #e3f1e8;
    border-radius: 8px;
    color: #28744a;
    display: flex;
    height: 42px;
    justify-content: center;
    width: 42px;
}

.decision-selected-file > div {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.decision-selected-file strong {
    color: #303446;
    font-size: 11px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.decision-selected-file small {
    color: #6d7383;
    font-size: 9px;
    margin-top: 3px;
}

.decision-visibility-card {
    align-items: center;
    background: #fff;
    border: 1px solid #e1e4ec;
    border-radius: 10px;
    cursor: pointer;
    display: grid;
    gap: 11px;
    grid-template-columns: 34px minmax(0, 1fr) auto;
    padding: 12px 14px;
    text-align: left;
    transition: background-color .15s ease, border-color .15s ease;
}

.decision-visibility-card:hover {
    background: #fbfcfd;
}

.decision-visibility-card.active {
    background: #f7fbf8;
    border-color: #a9cfb7;
}

.decision-visibility-card.active .decision-visibility-icon {
    background: #e3f1e8;
    color: #28744a;
}

.history-card-title {
    align-items: center;
    display: flex;
    justify-content: space-between;
}

.history-card-title > div:first-child {
    display: flex;
    flex-direction: column;
}

.history-card-title small {
    color: #73788a;
    font-size: 11px;
    font-weight: 400;
    margin-top: 3px;
}

.history-summary {
    align-items: center;
    display: flex;
    gap: 8px;
}

.history-summary > span {
    background: #f3f5f8;
    border: 1px solid #e5e7ed;
    border-radius: 999px;
    color: #6c7183;
    font-size: 9px;
    font-weight: 500;
    padding: 5px 9px;
    white-space: nowrap;
}

.history-summary strong {
    color: #25275d;
    font-size: 10px;
}

.history-card-body {
    background: #f8f9fb;
    padding: 16px !important;
}

.history-layout {
    align-items: start;
    display: grid;
    gap: 16px;
    grid-template-columns: minmax(0, 1.45fr) minmax(320px, .85fr);
}

.history-panel {
    background: #fff;
    border: 1px solid #e1e4ec;
    border-radius: 10px;
    overflow: hidden;
}

.history-panel-header {
    align-items: center;
    border-bottom: 1px solid #e8eaf0;
    display: flex;
    gap: 10px;
    min-height: 64px;
    padding: 12px 14px;
}

.history-panel-icon {
    align-items: center;
    background: #eef2f6;
    border-radius: 7px;
    color: #31536f;
    display: flex;
    flex: 0 0 auto;
    height: 34px;
    justify-content: center;
    width: 34px;
}

.history-panel-icon .v-icon {
    display: flex;
    line-height: 1;
    margin: 0;
}

.history-panel-header > div {
    display: flex;
    flex-direction: column;
}

.history-panel-header strong {
    color: #292c3e;
    font-size: 12px;
}

.history-panel-header small {
    color: #7a8091;
    font-size: 10px;
    margin-top: 2px;
}

.activity-feed {
    padding: 4px 16px 14px;
}

.activity-entry {
    display: grid;
    gap: 12px;
    grid-template-columns: 30px minmax(0, 1fr);
    min-height: 68px;
    padding-top: 14px;
    position: relative;
}

.activity-entry:not(:last-child)::before {
    background: #dce1e8;
    bottom: -2px;
    content: "";
    left: 15px;
    position: absolute;
    top: 42px;
    transform: translateX(-50%);
    width: 1px;
}

.activity-marker {
    align-items: center;
    background: #fff;
    border: 1px solid #cfd7e2;
    border-radius: 50%;
    color: #31536f;
    display: flex;
    height: 30px;
    justify-content: center;
    position: relative;
    width: 30px;
    z-index: 1;
}

.activity-marker .v-icon {
    display: flex;
    line-height: 1;
    margin: 0;
}

.activity-entry-content {
    border-bottom: 1px solid #eef0f4;
    padding: 2px 0 14px;
}

.activity-entry:last-child .activity-entry-content {
    border-bottom: 0;
}

.activity-entry-content > strong {
    color: #343747;
    display: block;
    font-size: 11px;
    line-height: 1.45;
}

.activity-meta {
    align-items: center;
    color: #818697;
    display: flex;
    flex-wrap: wrap;
    font-size: 9px;
    gap: 12px;
    margin-top: 6px;
}

.activity-meta span {
    align-items: center;
    display: inline-flex;
    gap: 4px;
    line-height: 1;
    min-height: 14px;
}

.activity-meta .v-icon {
    display: flex;
    flex: 0 0 auto;
    line-height: 1;
    margin: 0;
}

.comments-feed {
    display: grid;
    gap: 0;
    max-height: 330px;
    overflow-y: auto;
    padding: 4px 14px;
}

.comment-entry {
    display: grid;
    gap: 10px;
    grid-template-columns: 30px minmax(0, 1fr);
    padding: 12px 0;
}

.comment-entry + .comment-entry {
    border-top: 1px solid #eef0f4;
}

.comment-avatar {
    align-items: center;
    background: #e8eef4;
    border-radius: 50%;
    color: #31536f;
    display: flex;
    font-size: 9px;
    font-weight: 700;
    height: 30px;
    justify-content: center;
    line-height: 1;
    width: 30px;
}

.comment-author {
    align-items: baseline;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.comment-author strong {
    color: #343747;
    font-size: 10px;
}

.comment-author time {
    color: #9297a6;
    font-size: 8px;
}

.comment-content p {
    color: #5d6273;
    font-size: 10px;
    line-height: 1.5;
    margin: 4px 0 0;
    white-space: pre-wrap;
}

.history-empty-state {
    align-items: center;
    color: #8a8f9f;
    display: flex;
    flex-direction: column;
    gap: 6px;
    justify-content: center;
    min-height: 210px;
    padding: 20px;
    text-align: center;
}

.history-empty-state.compact {
    min-height: 140px;
}

.history-empty-state strong {
    color: #505466;
    font-size: 11px;
}

.history-empty-state span {
    font-size: 9px;
}

.comment-composer {
    background: #f8f9fb;
    border-top: 1px solid #e5e7ed;
    padding: 13px 14px;
}

.comment-composer > label {
    color: #343747;
    display: block;
    font-size: 10px;
    font-weight: 650;
    margin-bottom: 6px;
}

.comment-composer-footer {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-top: 9px;
}

.comment-composer-footer > span {
    align-items: center;
    color: #858a9b;
    display: inline-flex;
    font-size: 9px;
    gap: 4px;
}

.comment-composer-footer .v-icon,
.comment-composer-footer .v-btn .v-icon {
    display: flex;
    line-height: 1;
}

@media (max-width: 960px) {
    .section-navigation { position: static; }
    .process-heading { font-size: 19px; word-break: break-all; }
    .hero-fact { flex: 1 1 45%; min-width: 145px; }
    .history-layout { grid-template-columns: 1fr; }
    .history-card-title { align-items: flex-start; flex-direction: column; gap: 10px; }
    .decision-fields-grid { grid-template-columns: 1fr; }
    .decision-description-field { grid-column: auto; }
}
</style>
